import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, formatINR, formatDate, downloadReport, previewNumber } from "@/lib/api";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { VoucherScanLog } from "@/components/VoucherScanLog";
import { SearchableSelect } from "@/components/SearchableSelect";
import { DateRangeFilter, inRange } from "@/components/DateRangeFilter";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { AttachmentsField } from "@/components/AttachmentsField";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { Plus, FileSpreadsheet, FileText, Trash2, FileCheck2, Building2, Eye, FileDown, Download, ChevronDown, ChevronRight, Check, Lock, Paperclip } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { ProjectPIPanel } from "@/components/ProjectPIPanel";
import { BoqPoDeductionNote } from "@/components/BoqPoDeductionNote";
import { PrAttachmentsStrip } from "@/components/PrAttachmentsStrip";
import { toast, Toaster } from "sonner";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { BranchFilter } from "@/components/BranchFilter";
import { useVoucherFormOpen, discardWithConfirm } from "@/lib/voucherGuard";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { ReportingPeriodInput, ApprovedReportingPeriod, monthFromDate, parsePeriodText } from "@/components/ReportingPeriodField";

const TYPE_PILL = {
  Service:   { color: "bg-sky-100 text-sky-800" },
  Inventory: { color: "bg-emerald-100 text-emerald-800" },
  Asset:     { color: "bg-violet-100 text-violet-800" },
};

function SumStat({ label, value, testid, highlight }) {
  return (
    <div className="flex flex-col items-end" data-testid={testid}>
      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`tabular ${highlight ? "text-base font-bold text-[#F97316]" : "text-sm font-bold text-slate-900"}`}>
        {formatINR(value || 0)}
      </span>
    </div>
  );
}

// =========================================================================
// EinvPill — compact E-Invoice status pill rendered in the Invoice List
// table's new "E-Invoice" column. Three states:
//
//   • Done       — IRN generated successfully (emerald, with check)
//   • Pending    — no IRN yet (slate, dashed)
//   • Cancelled  — IRN was cancelled on the IRP (rose, with cross)
//
// Optionally surfaces the auto_drift_correction badge inline (tiny amber
// dot with hover tooltip) so accountants see at a glance which invoices
// had their line-rounding drift absorbed. Keeps the row compact — full
// drift badge is on the GST Centre page; here it's just a marker.
// =========================================================================
function EinvPill({ status, drift, rowId }) {
  if (!status) return <span className="text-slate-300">—</span>;
  const map = {
    Done:      { bg: "bg-emerald-50",  border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500" },
    Pending:   { bg: "bg-slate-50",    border: "border-slate-300",   text: "text-slate-600",   dot: "bg-slate-400"   },
    Cancelled: { bg: "bg-rose-50",     border: "border-rose-300",    text: "text-rose-800",    dot: "bg-rose-500"    },
  };
  const s = map[status] || map.Pending;
  const hasDrift = Math.abs(Number(drift || 0)) >= 0.005;
  return (
    <span data-testid={`einv-pill-${rowId}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${s.bg} ${s.border} ${s.text} text-[9px] font-bold uppercase tracking-wider whitespace-nowrap`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status}
      {hasDrift && (
        <span data-testid={`einv-drift-marker-${rowId}`}
          title={`Auto-reconciled ${Number(drift) < 0 ? "−" : "+"}₹${Math.abs(Number(drift)).toFixed(2)} paise drift into RndOffAmt during IRN generation (audit-trail only — no action needed).`}
          className="ml-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 cursor-help" />
      )}
    </span>
  );
}

export default function InvoicePage({ type }) {
  const { hasPerm } = useAuth();
  const [cancelPo, setCancelPo] = useState(null);
  const isPureSales = type === "sales";
  const isCN = type === "sales_credit_note";
  const isDN = type === "sales_debit_note";
  const isNote = isCN || isDN;
  const isPO = type === "purchase_order";
  // Treat real Sales Invoice + Credit Note + Debit Note as the same UI family
  const isSales = isPureSales || isNote;
  const resource = isCN ? "sales-credit-notes"
                : isDN ? "sales-debit-notes"
                : isPureSales ? "sales-invoices"
                : (isPO ? "purchase-orders" : "purchase-invoices");
  const partnerKey = isSales ? "client" : "vendor";
  const partnerLabel = isSales ? "Client" : "Vendor";
  const numberField = isNote ? "note_number" : (isPO ? "po_number" : "invoice_number");
  const dateField = isNote ? "note_date" : (isPO ? "po_date" : "invoice_date");
  const exportName = resource;
  const pageTitle = isCN ? "Sales Credit Notes"
                : isDN ? "Sales Debit Notes"
                : isPureSales ? "Sales Invoices"
                : (isPO ? "Purchase Orders" : "Purchase Invoices");
  const docNoun = isCN ? "Credit Note"
               : isDN ? "Debit Note"
               : isPO ? "Order"
               : "Invoice";

  const [search, setSearch] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [createdRange, setCreatedRange] = useState({ from: "", to: "" });
  const [filterProject, setFilterProject] = useState("");
  const [filterPartner, setFilterPartner] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  // E-Invoice status filter — applies to Sales Invoices, Credit Notes, Debit
  // Notes. Derived value: "Done" (IRN generated), "Pending" (no IRN),
  // "Cancelled" (IRN cancelled). Filter is hidden for purchase docs since
  // e-invoicing is a sales-side concept.
  const [filterEinv, setFilterEinv] = useState("");
  const [exportPrompt, setExportPrompt] = useState(null);  // { fmt: 'xlsx' | 'pdf' }

  const { active: activeBranch, branches } = useBranch();
  const [filterBranchTouched, setFilterBranchTouched] = useState(false);
  // Jun-2026 (user request) — Sales Invoice list defaults to ALL branches
  // (the Branch filter is still available); other doc types keep
  // inheriting the globally active branch.
  const effectiveBranch = filterBranchTouched ? filterBranch
    : (isPureSales ? "" : (activeBranch?.id || ""));

  // Aug-2026 — search is server-side across ALL pages (350ms debounce);
  // usePaginatedList resets to page 1 whenever params change.
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  const params = useMemo(() => ({
    ...(effectiveBranch ? { branch_id: effectiveBranch } : {}),
    ...(qDebounced ? { q: qDebounced } : {}),
  }), [effectiveBranch, qDebounced]);
  const { items: rawList, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList(`/${resource}`, params);
  const list = useMemo(() => (isNote
    ? (rawList || []).map(n => ({ ...n, invoice_number: n.note_number, invoice_date: n.note_date }))
    : rawList), [rawList, isNote]);
  const load = reload;
  const openFormTab = useFormTab();

  // Approval module key for the batch "pending status" lookup. Maps the page
  // `type` prop onto the backend's APPROVAL_MODULES vocabulary.
  const approvalModule = isCN ? "sales_credit_note"
                       : isDN ? "sales_debit_note"
                       : isPureSales ? "sales_invoice"
                       : isPO ? "purchase_order"
                       : "purchase_invoice";
  const pendingIds = useMemo(() => pendingDocIds(list), [list]);
  const pendingMap = useApprovalPendingMap(approvalModule, pendingIds);

  const labelNoun = isCN ? "Credit Note" : isDN ? "Debit Note" : isPO ? "Order" : "Invoice";

  const openNew = (prefilledKey = null) => openFormTab({
    path: `/${resource}/new${prefilledKey ? "/" + prefilledKey : ""}`,
    label: `New ${labelNoun}`,
    icon: Plus,
    parent: `/${resource}`,
    onSaved: load,
    // `saved` refreshes the parent list AND keeps this form tab open so the
    // user can immediately key the next invoice (Phase 36 Voucher Tab UX).
    render: ({ close, saved, viewList }) => (
      <InvoiceModal type={type} invoice={null} prefilled={prefilledKey} onClose={close} onSaved={saved} onViewList={viewList} />
    ),
  });
  const openExisting = (inv) => openFormTab({
    path: `/${resource}/edit/${inv.id}`,
    label: `${inv.status === "Approved" ? "View" : "Edit"} · ${inv.invoice_number || inv.note_number || labelNoun}`,
    icon: Eye,
    parent: `/${resource}`,
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <InvoiceModal type={type} invoice={inv} prefilled={null} onClose={close} onSaved={saved} onViewList={viewList} />
    ),
  });

  // Auto-spawn "New {labelNoun}" tab on landing.
  const autoSpawnedRef = useRef(false);
  useEffect(() => {
    if (autoSpawnedRef.current) return;
    autoSpawnedRef.current = true;
    openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDelete(id) {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/${resource}/${id}`);
    toast.success("Deleted");
    load();
  }

  async function handleSync(id) {
    await api.post(`/sales-invoices/${id}/voltledger-sync`);
    toast.success("Marked synced to VoltLedger");
    load();
  }

  async function handleApprove(id, num) {
    if (!window.confirm(`Approve PI ${num}? This will auto-create stock entries (Inventory items) and asset records (Asset items).`)) return;
    try {
      const r = await api.post(`/purchase-invoices/${id}/approve`);
      toast.success(`Approved · ${r.data.stock_entries_created} stock entries, ${r.data.assets_created} assets created`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  }

  async function handleCancelApproval(id, num) {
    if (!window.confirm(`Cancel approval of ${num}? This deletes the auto-created stock entries and assets (only if no asset is currently assigned).`)) return;
    try {
      await api.post(`/purchase-invoices/${id}/cancel-approval`);
      toast.success("Approval cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  }

  async function downloadVoucherPdf(inv) {
    try {
      const r = await api.get(`/${resource}/${inv.id}/voucher-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const num = inv[numberField] || inv.id;
      a.download = `${resource}-${String(num).replace(/[\\/]/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Voucher PDF downloaded");
    } catch (e) {
      let detail = "Download failed";
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* default */ }
      } else if (e?.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Billing"
        title={pageTitle}
        subtitle={isSales ? "Generate tax invoices for clients with GST, TDS, VoltLedger sync and pull-from-estimate." :
          (isPO ? "Track Purchase Orders to vendors with approval workflow and pull into Purchase Invoices." :
            "Track vendor purchase invoices with PO → GRN → Invoice flow.")}
        onRefresh={load}
        refreshing={loading}
        actions={
          <>
            {!isSales && !open && (
              <button data-testid={`${resource}-export`}
                onClick={() => setExportPrompt({ fmt: "xlsx", endpoint: `/exports/${resource}-excel`, filename: `${resource}.xlsx` })}
                className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                <Download size={14} /> Export Excel
              </button>
            )}
            <button data-testid={`${type}-download-xlsx-btn`}
              onClick={() => setExportPrompt({ fmt: "xlsx" })}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileSpreadsheet size={14} /> Report
            </button>
            <button data-testid={`${type}-download-pdf-btn`}
              onClick={() => setExportPrompt({ fmt: "pdf" })}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileText size={14} /> PDF
            </button>
            <button data-testid={`add-${type}-invoice-btn`} onClick={() => openNew()} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New {docNoun}
            </button>
          </>
        }
      />

      {/* Aug-2026 (user report) — never swallow the toolbar: when a search or
          filter is active and yields 0 rows, keep the search box + filters
          rendered (the table shows its "No matches" row) so the user can keep
          editing the query instead of landing on a blank page. */}
      {list.length === 0 && !qDebounced && !dateFrom && !dateTo && !createdRange.from && !createdRange.to && !filterProject && !filterPartner && !filterEinv && !filterBranchTouched
        ? <EmptyState title={isPO ? "No purchase orders yet" : "No invoices yet"} /> : (() => {
        const q = (search || "").trim().toLowerCase();
        // ---- E-Invoice derived status -----------------------------------
        // Single source of truth used by both the column cell and the filter
        // dropdown. Reads the SalesInvoice's `einvoice` sub-document which
        // is already shipped by the GET /sales-invoices list endpoint:
        //   • cancel_status === "Cancelled"  → "Cancelled"  (rose pill)
        //   • irn present                    → "Done"       (emerald pill)
        //   • otherwise                      → "Pending"    (slate pill)
        // Returns "" for non-sales documents (PO / PI) so the column hides.
        const einvStatus = (inv) => {
          if (!isSales) return "";
          const ei = inv.einvoice || {};
          if ((ei.cancel_status || "") === "Cancelled") return "Cancelled";
          if (ei.irn) return "Done";
          return "Pending";
        };
        // Derive unique Project + Partner options from the loaded list
        const uniqueProjects = Array.from(new Set(list.map(i => i.project_name).filter(Boolean))).sort();
        const uniquePartners = Array.from(new Set(list.map(i => i[`${partnerKey}_name`]).filter(Boolean))).sort();
        const filtered = list.filter(inv => {
          if (filterBranch && inv.branch_id !== filterBranch) return false;
          if (filterProject && inv.project_name !== filterProject) return false;
          if (filterPartner && inv[`${partnerKey}_name`] !== filterPartner) return false;
          if (filterEinv && einvStatus(inv) !== filterEinv) return false;
          // Date-range filter (against the doc's date field)
          const d = inv[dateField] ? String(inv[dateField]).slice(0, 10) : "";
          if (dateFrom && d && d < dateFrom) return false;
          if (dateTo && d && d > dateTo) return false;
          // Created-date filter (independent of the doc date)
          if (!inRange(inv.created_at, createdRange)) return false;
          if (!q) return true;
          const hay = [
            inv[numberField], inv[dateField], inv.branch_name,
            inv[`${partnerKey}_name`], inv.project_name, inv.ref_no,
            inv.alternate_order_no,
            inv.status, inv.payment_status, inv.po_number, inv.grn_number,
            inv.bill_type, inv.estimate_number, inv.transporter_name, inv.vehicle_no,
            inv.notes,
            ...(inv.line_items || []).flatMap(li => [li.item_code, li.description]),
            isSales ? einvStatus(inv) : "",
          ].filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        });
        // Aggregate totals for header strip
        const sumSubtotal = filtered.reduce((s, inv) => s + Number(inv.subtotal || 0), 0);
        const sumGST = filtered.reduce((s, inv) => s + Number(inv.tax_amount || 0), 0);
        const sumTDS = filtered.reduce((s, inv) => s + Number(inv.tds_amount || 0), 0);
        const sumTotal = filtered.reduce((s, inv) => s + Number(inv.total_amount || 0), 0);
        const isFiltered = !!(q || dateFrom || dateTo || createdRange.from || createdRange.to || filterProject || filterPartner || filterBranch || filterEinv);
        return (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center justify-between gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-sm">
              <input data-testid={`${type}-list-search`} type="search"
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${pageTitle.toLowerCase()} by no, ${partnerLabel.toLowerCase()}, project, ref, status…`}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white" />
              <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            </div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 flex-wrap">
              <BranchFilter value={effectiveBranch} onChange={(v) => { setFilterBranchTouched(true); setFilterBranch(v); }} testid={`${type}-list-filter-branch`} />
              {/* Jun-2026 — project filter is searchable (type to find) */}
              <SearchableSelect
                value={filterProject}
                onChange={(v) => setFilterProject(v || "")}
                items={uniqueProjects.map(p => ({ id: p, name: p }))}
                placeholder="All Projects"
                searchPlaceholder="Search project…"
                className="min-w-[160px] normal-case"
                inputClassName="px-2 py-1.5 text-xs"
                testid={`${type}-list-filter-project`}
              />
              {/* Jun-2026 — vendor/customer filter is searchable (type to find) */}
              <SearchableSelect
                value={filterPartner}
                onChange={(v) => setFilterPartner(v || "")}
                items={uniquePartners.map(p => ({ id: p, name: p }))}
                placeholder={`All ${partnerLabel}s`}
                searchPlaceholder={`Search ${partnerLabel.toLowerCase()}…`}
                className="min-w-[160px] normal-case"
                inputClassName="px-2 py-1.5 text-xs"
                testid={`${type}-list-filter-partner`}
              />
              {isSales && (
                <select data-testid={`${type}-list-filter-einv`} value={filterEinv}
                  onChange={e => setFilterEinv(e.target.value)}
                  title="E-Invoice IRN status"
                  className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none normal-case min-w-[130px]">
                  <option value="">All E-Invoice</option>
                  <option value="Done">E-Invoice · Done</option>
                  <option value="Pending">E-Invoice · Pending</option>
                  <option value="Cancelled">E-Invoice · Cancelled</option>
                </select>
              )}
              <span>From</span>
              <input data-testid={`${type}-list-date-from`} type="date" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none normal-case" />
              <span>To</span>
              <input data-testid={`${type}-list-date-to`} type="date" value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none normal-case" />
              <DateRangeFilter value={createdRange} onChange={setCreatedRange} testidPrefix={type} label="Created" />
              {isFiltered && (
                <button onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setCreatedRange({from:"",to:""}); setFilterProject(""); setFilterPartner(""); setFilterBranch(""); setFilterBranchTouched(false); setFilterEinv(""); }}
                  data-testid={`${type}-list-clear-filters`}
                  className="ml-1 text-[#F97316] hover:underline normal-case">Clear</button>
              )}
            </div>
          </div>
          <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center justify-between gap-4 flex-wrap" data-testid={`${type}-list-summary`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500" data-testid={`${type}-list-count`}>
              {isFiltered ? `${filtered.length} of ${total} ${pageTitle.toLowerCase()}` : `${total} ${pageTitle.toLowerCase()}`}
            </div>
            <div className="flex items-center gap-5 flex-wrap text-xs">
              <SumStat label="Subtotal" value={sumSubtotal} testid={`${type}-sum-subtotal`} />
              <SumStat label="Total GST" value={sumGST} testid={`${type}-sum-gst`} />
              {!isPO && <SumStat label="Total TDS" value={sumTDS} testid={`${type}-sum-tds`} />}
              <SumStat label="Total Amount" value={sumTotal} testid={`${type}-sum-total`} highlight />
            </div>
          </div>
          <div>
            <table className="w-full table-auto" data-testid={`${type}-invoice-table`}>
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  {[
                    isPO ? "PO No" : "Inv No", "Date",
                    // Jun-2026 (user request) — SI list swaps the Branch
                    // column (redundant with the Branch filter) for the
                    // Alternate Order No.
                    isPureSales ? "Alt Order No" : "Branch",
                    partnerLabel, "Project", "Total",
                    ...(isPureSales ? ["Approval", "E-Invoice", "VoltLedger"]
                       : isNote     ? ["Approval", "E-Invoice", "Ref Inv"]
                       :              ["Approval"]),
                  ].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2 whitespace-nowrap">{h}</th>
                  )}
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="20" className="py-10 text-center text-sm text-slate-400">
                    No matches{q ? <> for <span className="font-bold text-slate-700">&quot;{search}&quot;</span></> : ""}{(dateFrom || dateTo) ? " in the selected date range" : ""}
                  </td></tr>
                ) : filtered.map(inv => {
                  const ei = isSales ? einvStatus(inv) : "";
                  // Amounts tooltip — kept here so the user can still see the
                  // subtotal / GST / TDS breakdown without us widening the row.
                  const amtTip = `Subtotal: ${formatINR(inv.subtotal)} · GST: ${formatINR(inv.tax_amount)}` +
                    (!isPO ? ` · TDS: ${formatINR(inv.tds_amount)}` : "");
                  return (
                  <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2 px-2 text-[11px] font-bold text-slate-900 font-mono whitespace-nowrap"
                        title={inv.estimate_number ? `From Estimate: ${inv.estimate_number}` : ""}>
                      {inv[numberField]}
                      {inv.estimate_number && (
                        <span className="ml-1 text-[8px] uppercase tracking-wider text-slate-400 font-bold align-top" title={`From Estimate: ${inv.estimate_number}`}>EST</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-slate-600 whitespace-nowrap">{formatDate(inv[dateField])}</td>
                    <td className="py-2 px-2 text-[11px]">
                      {isPureSales ? (
                        <span className="text-slate-700 max-w-[160px] truncate inline-block align-middle"
                              data-testid={`alt-order-cell-${inv.id}`}
                              title={inv.alternate_order_no || ""}>
                          {inv.alternate_order_no || "—"}
                        </span>
                      ) : (
                        inv.branch_name ? <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-1.5 py-0.5 font-bold uppercase tracking-wider text-[9px]"><Building2 size={9}/> {inv.branch_name}</span> : "—"
                      )}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 max-w-[180px] truncate" title={inv[`${partnerKey}_name`] || ""}>{inv[`${partnerKey}_name`]}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 max-w-[160px] truncate" title={inv.project_name || ""}>{inv.project_name}</td>
                    <td className="py-2 px-2 text-[11px] tabular font-bold whitespace-nowrap" title={amtTip}>{formatINR(inv.total_amount)}</td>
                    {isSales && (
                      <td className="py-2 px-2">
                        <PendingApprovalBadge status={inv.status || "Pending"} pendingInfo={pendingMap[inv.id]} />
                      </td>
                    )}
                    {isSales && (
                      <td className="py-2 px-2" data-testid={`einv-cell-${inv.id}`}>
                        <EinvPill status={ei} drift={inv.einvoice?.auto_drift_correction} rowId={inv.id} />
                      </td>
                    )}
                    {isPureSales && (
                      <td className="py-2 px-2">
                        {inv.voltledger_sync_status === "Synced" ? <StatusBadge status="Synced" /> : (
                          <button data-testid={`vlsync-${inv.id}`} onClick={() => handleSync(inv.id)} className="text-[9px] font-bold uppercase tracking-wider border border-[#F97316] text-[#F97316] px-1.5 py-0.5 hover:bg-[#F97316] hover:text-white transition-colors">
                            Sync →
                          </button>
                        )}
                      </td>
                    )}
                    {isNote && (
                      <td className="py-2 px-2 text-[11px] text-slate-600 max-w-[140px] truncate" title={inv.reference_invoice_number || ""}>{inv.reference_invoice_number || "—"}</td>
                    )}
                    {!isSales && !isPO && (
                      <td className="py-2 px-2">
                        {inv.status === "Approved" ? (
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status="Approved" />
                            <button data-testid={`unapprove-${inv.id}`} onClick={() => handleCancelApproval(inv.id, inv.invoice_number)}
                                    className="text-[9px] text-red-600 hover:underline uppercase tracking-wider font-bold">
                              Cancel
                            </button>
                          </div>
                        ) : pendingMap[inv.id] ? (
                          <PendingApprovalBadge status={inv.status || "Pending"} pendingInfo={pendingMap[inv.id]} />
                        ) : (
                          <button data-testid={`approve-${inv.id}`} onClick={() => handleApprove(inv.id, inv.invoice_number)}
                                  className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 hover:bg-emerald-700">
                            Approve →
                          </button>
                        )}
                      </td>
                    )}
                    {isPO && (
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {inv.procurement_flow === "PROJECT_OPERATIONS" ? (
                            <span data-testid={`po-projops-${inv.id}`}
                              title={`Project Operations — converted from PR ${inv.source_pr_number || ""}. ${inv.status === "Approved" ? "Approved — GRN receiving opens after the stock-isolation phase." : inv.status === "Pending" ? "Pending PO approval." : inv.status === "Rejected" ? "Rejected — edit and resubmit." : "Draft."}`}
                              className="inline-block bg-sky-100 border border-sky-300 text-sky-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider cursor-help">
                              Project Ops · {inv.status}
                            </span>
                          ) : (
                            <PendingApprovalBadge status={inv.status || "Pending"} pendingInfo={pendingMap[inv.id]} />
                          )}
                          {inv.cancellation_status === "Cancellation Pending" && (
                            <span data-testid={`po-cancel-pending-${inv.id}`}
                              title={`Cancellation requested by ${inv.cancellation_requested_by || ""} — reason: ${inv.cancellation_reason || ""}`}
                              className="inline-block bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider cursor-help">
                              Cancellation Pending
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="py-2 px-2 text-[11px] whitespace-nowrap text-right">
                      {pendingMap[inv.id] && (
                        <span className="mr-1.5 inline-block align-middle">
                          <TakeActionButton pendingInfo={pendingMap[inv.id]} testid={`take-action-${inv.id}`} />
                        </span>
                      )}
                      {(() => {
                        // GST compliance: once IRN is live, edit + delete are
                        // blocked even at admin level (backend enforces with
                        // HTTP 423). The UI hides the destructive controls
                        // and swaps in a passive lock indicator so the
                        // operator never even gets to fire a request that
                        // would just bounce. View becomes read-only access
                        // to the locked record. The Cancelled IRN path
                        // reopens the row — same logic as backend
                        // `_is_einvoice_locked`.
                        const ei = inv.einvoice || {};
                        const irnLocked = isSales && !!ei.irn && (ei.cancel_status || "") !== "Cancelled";
                        return (
                          <>
                            <button data-testid={`view-inv-${inv.id}`} onClick={() => openExisting(inv)}
                              className="text-slate-500 hover:text-[#F97316] inline-flex items-center gap-1 mr-2"
                              title={irnLocked ? "View (read-only — IRN locked)" : "View / Edit"}>
                              <Eye size={13} />
                            </button>
                            <button data-testid={`pdf-inv-${inv.id}`} onClick={() => downloadVoucherPdf(inv)}
                              className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 mr-2"
                              title={isSales ? "Download Tax Invoice PDF" : "Download Voucher PDF"}>
                              <FileDown size={13} />
                            </button>
                            {isPO && inv.status === "Approved" &&
                              inv.cancellation_status !== "Cancellation Pending" &&
                              hasPerm("po_cancellation", "create") && (
                              <button data-testid={`cancel-po-${inv.id}`}
                                onClick={() => setCancelPo(inv)}
                                title="Request cancellation of this approved PO (flows the approval matrix)"
                                className="text-amber-700 hover:underline text-[11px] mr-2">
                                Cancel PO
                              </button>
                            )}
                            {irnLocked ? (
                              <span data-testid={`irn-locked-${inv.id}`}
                                title={`Locked — IRN ${(ei.irn || "").slice(0, 16)}… is live on the IRP. To edit/delete, first cancel the IRN on the GST Centre (within 24h) or raise a Credit Note.`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 border border-slate-300 text-slate-600 text-[9px] font-bold uppercase tracking-wider cursor-help">
                                <Lock size={9} /> IRN Locked
                              </span>
                            ) : inv.procurement_flow === "PROJECT_OPERATIONS" ? (
                              <span data-testid={`projops-no-delete-${inv.id}`}
                                title="Project Operations Draft POs cannot be deleted — use the controlled 'Cancel Draft Conversion' action in the Project Workspace (Vendor Management)."
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 border border-slate-300 text-slate-600 text-[9px] font-bold uppercase tracking-wider cursor-help">
                                <Lock size={9} /> Controlled
                              </span>
                            ) : (
                              <button data-testid={`delete-inv-${inv.id}`}
                                onClick={() => handleDelete(inv.id)}
                                className="text-red-600 hover:underline text-[11px]">
                                Delete
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
        );
      })()}

      {exportPrompt && (
        <DateRangeExportModal
          fmt={exportPrompt.fmt}
          exportName={exportName}
          defaultFrom={dateFrom}
          defaultTo={dateTo}
          onClose={() => setExportPrompt(null)}
          onConfirm={async (from_d, to_d) => {
            try {
              if (exportPrompt.endpoint) {
                const params = new URLSearchParams();
                if (from_d) params.set("from_date", from_d);
                if (to_d) params.set("to_date", to_d);
                const qs = params.toString() ? `?${params.toString()}` : "";
                const r = await api.get(`${exportPrompt.endpoint}${qs}`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                const stamp = (from_d || to_d) ? `-${from_d || "all"}_to_${to_d || "now"}` : "";
                a.download = (exportPrompt.filename || `${exportName}${stamp}.xlsx`).replace(/\.xlsx$/, `${stamp}.xlsx`);
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } else {
                await downloadReport(exportName, exportPrompt.fmt, { from_date: from_d, to_date: to_d });
              }
              toast.success(`${exportPrompt.fmt.toUpperCase()} downloaded`);
            } catch (e) {
              toast.error("Download failed");
            }
            setExportPrompt(null);
          }}
        />
      )}
      {cancelPo && (
        <CancelPoModal po={cancelPo} onClose={() => setCancelPo(null)}
          onSaved={() => { setCancelPo(null); load(); }} />
      )}
    </div>
  );
}

// Jun-2026 (user request) — post-approval PO cancellation: captures the
// mandatory reason and raises a `po_cancellation` approval request.
function CancelPoModal({ po, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!reason.trim()) { toast.error("Cancellation reason is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${po.id}/request-cancellation`, { reason: reason.trim() });
      const st = r.data?._approval_request?.status;
      toast.success(st === "Approved"
        ? `${po.po_number} cancelled`
        : `${po.po_number} — cancellation sent for approval`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to request cancellation");
    } finally { setBusy(false); }
  }
  return createPortal(
    <div className="fixed inset-0 w-screen h-screen bg-black/50 z-[9999] flex items-center justify-center p-4" data-testid="cancel-po-modal" style={{ position: "fixed", inset: 0 }}>
      <div className="bg-white w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-lg font-bold">Cancel Purchase Order</h3>
          <p className="text-xs text-slate-500 mt-1">{po.po_number} · {po.vendor_name} — the cancellation flows the approval matrix; the PO stays Approved until the final approver signs off.</p>
        </div>
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reason for cancellation <span className="text-red-600">*</span></div>
          <textarea data-testid="cancel-po-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            rows={3} placeholder="e.g. vendor cannot supply, duplicate order, project descoped…"
            className="w-full border border-slate-300 px-2.5 py-2 text-xs focus:outline-none focus:border-orange-500" />
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="cancel-po-close" onClick={onClose}
            className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Keep PO</button>
          <button data-testid="cancel-po-submit" disabled={busy} onClick={submit}
            className="bg-red-700 hover:bg-red-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
            {busy ? "Submitting…" : "Request Cancellation"}
          </button>
        </div>
      </div>
    </div>, document.body);
}

function DateRangeExportModal({ fmt, exportName, defaultFrom, defaultTo, onClose, onConfirm }) {
  const [from, setFrom] = useState(defaultFrom || "");
  const [to, setTo] = useState(defaultTo || "");
  const [busy, setBusy] = useState(false);
  return createPortal(
    <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 z-[9999] flex items-center justify-center p-4" data-testid="export-date-range-modal" style={{ position: "fixed", inset: 0 }}>
      <div className="bg-white w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-lg font-bold">Download {fmt.toUpperCase()} — Select Date Range</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Up to <b>3,000 rows</b> per download — narrow the range if needed.</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              data-testid="export-date-from"
              className="w-full px-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              data-testid="export-date-to"
              className="w-full px-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-wider font-bold">
            {[
              ["This Month", () => { const d = new Date(); setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)); setTo(new Date().toISOString().slice(0, 10)); }],
              ["Last 30 Days", () => { setFrom(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)); setTo(new Date().toISOString().slice(0, 10)); }],
              ["FY 26-27", () => { setFrom("2026-04-01"); setTo("2027-03-31"); }],
              ["All Time", () => { setFrom(""); setTo(""); }],
            ].map(([label, fn]) => (
              <button key={label} type="button" onClick={fn}
                className="px-2 py-1 bg-slate-100 hover:bg-orange-100 hover:text-[#F97316] text-slate-600">{label}</button>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900">Cancel</button>
          <button data-testid="export-confirm-btn" disabled={busy}
            onClick={async () => { setBusy(true); await onConfirm(from || null, to || null); setBusy(false); }}
            className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 text-white">
            {busy ? "Downloading…" : `Download ${fmt.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ProjOpsRoutingSummary({ po }) {
  const lines = (po.line_items || []).filter(l => l.routing_snapshot);
  if (!lines.length) return null;
  const counts = { grn: 0, asset: 0, service: 0 };
  lines.forEach(l => {
    const s = l.routing_snapshot;
    if (s.grn_route) counts.grn += 1;
    else if (s.pi_asset_route) counts.asset += 1;
    else if (s.pi_service_route) counts.service += 1;
  });
  return (
    <div data-testid="projops-routing-summary" className="border border-sky-200 bg-sky-50/50 px-5 py-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-sky-900">
          Approved Line Routing — frozen v{po.po_approval_version || 1}
        </div>
        <div className="flex gap-2 text-[10px] font-bold">
          <span data-testid="routing-count-grn" className="bg-emerald-100 border border-emerald-300 text-emerald-800 px-2 py-0.5">GRN-routed: {counts.grn}</span>
          <span data-testid="routing-count-asset" className="bg-violet-100 border border-violet-300 text-violet-800 px-2 py-0.5">Asset: {counts.asset}</span>
          <span data-testid="routing-count-service" className="bg-sky-100 border border-sky-300 text-sky-800 px-2 py-0.5">Service: {counts.service}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] mt-3">
          <thead>
            <tr className="text-left text-slate-500 uppercase text-[9px] tracking-wider">
              <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Item</th><th className="py-1 pr-2">UOM</th>
              <th className="py-1 pr-2">Routing</th><th className="py-1 pr-2">GRN Required</th>
              <th className="py-1 pr-2">Future PI Route</th><th className="py-1 pr-2">Ver</th>
              <th className="py-1 pr-2">Frozen</th><th className="py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => { const s = l.routing_snapshot; return (
              <tr key={s.po_line_uid || i} data-testid={`routing-line-${i}`} className="border-t border-sky-100">
                <td className="py-1.5 pr-2 text-slate-500">{i + 1}</td>
                <td className="py-1.5 pr-2 font-bold">{s.item_code} — {s.item_name}</td>
                <td className="py-1.5 pr-2">{s.uom}</td>
                <td className="py-1.5 pr-2">{s.routing_type}</td>
                <td className="py-1.5 pr-2">{s.grn_route ? <span className="text-emerald-700 font-bold">Yes — GRN (Phase 3C)</span> : "No"}</td>
                <td className="py-1.5 pr-2">{s.pi_asset_route ? "Asset PI (Phase 4)" : s.pi_service_route ? "Service PI (Phase 4)" : "Inventory PI after GRN (Phase 4)"}</td>
                <td className="py-1.5 pr-2">v{s.snapshot_version}</td>
                <td className="py-1.5 pr-2">{(s.frozen_at || "").slice(0, 10)}</td>
                <td className="py-1.5">{s.status === "active" ? <span className="text-emerald-700 font-bold uppercase text-[9px]">Active</span> : s.status}</td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">
        Routing was frozen at PO approval — later Item Master changes do not affect this PO. GRN receiving and Purchase Invoicing remain unavailable during Phase 3A.
      </div>
    </div>
  );
}

export function InvoiceModal({ type, invoice, prefilled, onClose, onSaved, onViewList }) {
  const navigate = useNavigate();
  useVoucherFormOpen();
  const mastersRefreshKey = useMastersRefresh();
  const isPureSales = type === "sales";
  const isCN = type === "sales_credit_note";
  const isDN = type === "sales_debit_note";
  const isNote = isCN || isDN;
  const isSales = isPureSales || isNote;
  const noteNoun = isCN ? "Credit Note" : isDN ? "Debit Note" : "";
  const label = invoice
    ? `this ${noteNoun || (isSales ? "sales" : "purchase")} ${noteNoun ? "" : "invoice"} edit`.trim()
    : `this new ${noteNoun || (isSales ? "sales" : "purchase")} ${noteNoun ? "" : "invoice"}`.trim();
  // Discard exits to the application dashboard (not the list).
  const handleDiscard = () => discardWithConfirm(() => navigate("/"), label);
  const { active: activeBranch, branches } = useBranch();
  const isPO = type === "purchase_order";
  const docNoun = isCN ? "Credit Note" : isDN ? "Debit Note" : isPO ? "Order" : "Invoice";
  const resource = isCN ? "sales-credit-notes"
                : isDN ? "sales-debit-notes"
                : isPureSales ? "sales-invoices"
                : (isPO ? "purchase-orders" : "purchase-invoices");
  const numberField = isNote ? "note_number" : (isPO ? "po_number" : "invoice_number");
  const dateField = isNote ? "note_date" : (isPO ? "po_date" : "invoice_date");
  const partnerKey = isSales ? "client" : "vendor";
  const partnerResource = isSales ? "clients" : "vendors";

  // Edit mode: seed the picker with the voucher's saved partner IMMEDIATELY
  // so the Vendor/Client field always displays — even before the picker API
  // responds, and even if it fails (live incident: converted Draft POs
  // intermittently showed a blank Vendor when one master call errored).
  const [partners, setPartners] = useState(() =>
    invoice && invoice[`${partnerKey}_id`] ? [{
      id: invoice[`${partnerKey}_id`],
      name: invoice[`${partnerKey}_name`] || "(saved partner)",
      [`${partnerKey}_code`]: invoice[`${partnerKey}_code`] || invoice.vendor_code || "",
      gst_number: invoice.gst_number || "",
    }] : []);
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  // Jun-2026 — open PO Payment Requests for the PPR dropdown on PI forms.
  const [openPayReqs, setOpenPayReqs] = useState([]);
  // Sprint 4.1 Phase B — Warehouse picker, branch-scoped + respects the
  // current user's allowed_warehouse_ids (server-side filter).
  const [warehouses, setWarehouses] = useState([]);
  const [openRepairs, setOpenRepairs] = useState([]);
  const [approvedEstimates, setApprovedEstimates] = useState([]);
  const [estimatePicker, setEstimatePicker] = useState(prefilled === "PICK_ESTIMATE");
  const [poPickerOpen, setPoPickerOpen] = useState(false);
  // Mother Invoice picker (only for Credit Note / Debit Note)
  const [motherInvoices, setMotherInvoices] = useState([]);
  // In-progress search text for autocomplete pickers (null = use bound display label)
  const [projectSearch, setProjectSearch] = useState(null);
  const [rrnSearch, setRrnSearch] = useState(null);

  const emptyLine = { item_id: "", item_code: "", description: "", serial_no: "", batch_no: "", pack_unit: "Box", pack_qty: 1, quantity: 0, unit: "Nos", base_rate: 0, rate: 0, gst_rate: 18, amount: 0 };
  // Normalize the inbound `invoice` (existing record). For PO mode the backend
  // stores `po_number` / `po_date` instead of `invoice_number` / `invoice_date`.
  // We mirror them onto the canonical state fields so the form code stays unified.
  const _normalized = invoice ? (isPO ? {
    ...invoice,
    invoice_number: invoice.po_number || invoice.invoice_number || "",
    invoice_date: invoice.po_date || invoice.invoice_date || "",
  } : isNote ? {
    ...invoice,
    invoice_number: invoice.note_number || invoice.invoice_number || "",
    invoice_date: invoice.note_date || invoice.invoice_date || "",
  } : invoice) : null;
  // Factory for a blank draft — used on mount AND after a successful CREATE
  // when we wipe the form so the user can immediately key the next invoice
  // in the same tab (Phase 36 Voucher Tab UX).
  const blankDraft = () => ({
    invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10),
    branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || "",
    [`${partnerKey}_id`]: "", project_id: "",
    sales_gst_type: "Tax Invoice", document_type_credit: "Credit",
    location: "", category: "", linked_repair_id: "", linked_repair_number: "", warehouse: "",
    warehouse_id: "", warehouse_name: "", email: "",
    currency: "INR", conversion_rate: 1,
    sub_vendor_name: "",
    salesman: "", service_period: "", agent: "", tax_treatment: "Exclusive",
    reporting_period: isPureSales ? new Date().toISOString().slice(0, 7) : "",
    payment_terms: "30 Days", due_date: "", jms_number: "",
    ref_no: "", ref_date: "", rc_number: "",
    alternate_order_no: "", alternate_order_no_date: "",
    billing_address: "", shipping_address: "", gst_number: "",
    estimate_id: "", estimate_number: "",
    line_items: [emptyLine],
    tds_amount: 0, payment_status: "Pending",
    attachments: [],
    ...(isSales ? {
      work_order_no: "",
      bill_type: "Credit",
      enable_tds: true, tds_rate: 2.0,
      enable_round_up: true,
      special_deductions: [
        { name: "", amount: 0, enabled: false },
        { name: "", amount: 0, enabled: false },
        { name: "", amount: 0, enabled: false },
      ],
      transport_mode: "", vehicle_no: "", vehicle_type: "", transporter_name: "",
      transporter_id: "", lr_no: "", lr_date: "", distance_km: 0,
      dispatch_from: "", delivery_to: "", sub_supply_type: "",
      generate_eway: false, eway_bill_no: "",
    } : { po_number: "", grn_number: "", payment_request_number: "", enable_round_up: true }),
    ...(isPO ? { expected_delivery_date: "", enable_round_up: true } : {}),
  });
  const [form, setForm] = useState(_normalized || blankDraft());
  const [saving, setSaving] = useState(false);
  // Jun-2026 — "Create PI" shortcut from a PO Payment Request: pre-fill the
  // new PI from the request's PO (all remaining qty) + link the PPR number.
  const pprPrefillRef = useRef(false);
  useEffect(() => {
    if (pprPrefillRef.current || invoice) return;
    if (!prefilled || typeof prefilled !== "object" || prefilled.kind !== "FROM_PPR") return;
    pprPrefillRef.current = true;
    const req = prefilled.request || {};
    (async () => {
      try {
        const params = req.project_id ? { project_id: req.project_id } : {};
        const r = await api.get("/purchase-orders/_pickable", { params });
        const po = (r.data || []).find((p) => p.id === req.po_id);
        if (!po) {
          toast.error(`PO ${req.po_number || ""} has nothing left to pull (fully invoiced, or you lack access) — use 'Pull from PO' manually if needed.`);
          return;
        }
        const newLines = (po.line_items || [])
          .filter((l) => Number(l._remaining_quantity || 0) > 0.001)
          .map((l) => ({
            item_id: l.item_id || "", item_code: l.item_code || "",
            description: l.description || "",
            quantity: Number(l._remaining_quantity || 0),
            unit: l.unit || "Nos",
            base_rate: Number(l.rate || 0), rate: Number(l.rate || 0),
            gst_rate: Number(l.gst_rate || 0),
            amount: Number((l._remaining_quantity || 0) * (l.rate || 0)),
            item_type: l.item_type || "", pack_qty: 1, pack_unit: "Box",
            source_po_id: po.id, source_po_number: po.po_number,
            source_po_line_index: l._po_line_index,
            pi_line_uid: "", pi_ahead_reason: "",
            _remaining_quantity: l._remaining_quantity,
          }));
        setForm((f) => ({
          ...f,
          vendor_id: po.vendor_id || f.vendor_id,
          vendor_name: po.vendor_name || f.vendor_name,
          project_id: po.project_id || f.project_id,
          project_name: po.project_name || f.project_name,
          branch_id: po.branch_id || f.branch_id,
          branch_name: po.branch_name || f.branch_name,
          po_number: po.po_number || f.po_number,
          payment_request_number: req.request_number || f.payment_request_number,
          _pulled_from_po: po.po_number,
          line_items: newLines,
        }));
        setPartners((prev) => prev.some((x) => x.id === po.vendor_id) ? prev
          : [...prev, { id: po.vendor_id, name: po.vendor_name || "(vendor)" }]);
        toast.success(`Pre-filled from ${po.po_number} — ${newLines.length} line(s), linked to ${req.request_number}`);
      } catch {
        toast.error("Could not pre-fill from the PO — use 'Pull from PO' manually.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilled]);
  // Phase 3A — Project Operations PO submission state
  const isProjOpsPo = type === "purchase_order" && invoice?.procurement_flow === "PROJECT_OPERATIONS";
  const projOpsLocked = isProjOpsPo && ["Pending", "Approved", "Cancelled"].includes(invoice?.status || "");
  const [projOpsApprovalOn, setProjOpsApprovalOn] = useState(false);
  const [submittingPo, setSubmittingPo] = useState(false);
  useEffect(() => {
    if (isProjOpsPo) {
      api.get("/project-ops/status")
        .then(r => setProjOpsApprovalOn(!!r.data?.po_approval_enabled))
        .catch(() => {});
    }
  }, [isProjOpsPo, invoice?.id]);
  // Jun-2026 (owner-approved) — amend an APPROVED Project Ops PO: returns it
  // to Draft (reason mandatory) and it re-runs the normal approval matrix.
  const amendProjOpsPo = async () => {
    const reason = window.prompt("Amendment reason (mandatory) — the PO will return to Draft and follow the approval matrix again. GRN receiving / new PI pulls pause until it is re-approved:");
    if (!reason || !reason.trim()) return;
    try {
      setSubmittingPo(true);
      await api.post(`/purchase-orders/${invoice.id}/amend`, { reason: reason.trim() });
      toast.success("PO moved to Draft for amendment — edit it and resubmit for approval");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amendment failed");
    } finally {
      setSubmittingPo(false);
    }
  };
  const submitProjOpsPo = async () => {
    try {
      setSubmittingPo(true);
      await api.post("/approvals/submit", {
        module: "purchase_order",
        document_id: invoice.id,
        document_number: invoice.po_number || "",
        branch_id: invoice.branch_id || "",
      });
      toast.success("Project Operations PO submitted for approval");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmittingPo(false);
    }
  };
  // Jun-2026 — Direct Ops PO (no PR): flow selector on the NEW PO form.
  // Availability = global ops flag + project_direct_ops_po flag + PO create
  // permission (server-decided); projects = ops-enabled + user-accessible.
  const [poFlow, setPoFlow] = useState("standard");
  const [directOps, setDirectOps] = useState({ enabled: false, projects: [] });
  useEffect(() => {
    if (isPO && !invoice) {
      api.get("/direct-ops-po/availability")
        .then(r => setDirectOps(r.data || { enabled: false, projects: [] }))
        .catch(() => {});
    }
  }, [isPO, invoice]);
  const opsDirectCreate = isPO && !invoice && poFlow === "ops" && directOps.enabled;
  const cancelDirectOpsPo = async () => {
    const reason = window.prompt("Reason for cancelling this Direct Ops PO (mandatory — kept in history):");
    if (!reason || !reason.trim()) return;
    try {
      setSubmittingPo(true);
      await api.post(`/projects/${invoice.project_id}/direct-ops-po/${invoice.id}/cancel`, { reason: reason.trim() });
      toast.success("Direct Ops PO cancelled");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setSubmittingPo(false); }
  };
  // Phase C-Lite — Project Reporting Period smart default (SI create only):
  // stays synced to the invoice month until the operator touches the field
  // or an estimate pull derives a better value (work-completion month /
  // parseable service period).
  const rpTouchedRef = useRef(false);
  useEffect(() => {
    if (invoice || !isPureSales || rpTouchedRef.current) return;
    const m = monthFromDate(form.invoice_date);
    if (m && m !== form.reporting_period) set("reporting_period", m);
    // eslint-disable-next-line
  }, [form.invoice_date]);
  // GST-compliance lock — derived from the editing record. When the
  // SalesInvoice / Credit Note / Debit Note carries a live IRN
  // (einvoice.irn present AND einvoice.cancel_status !== "Cancelled"),
  // every line item and the Save action go read-only. The backend
  // enforces the same rule with HTTP 423 (`_assert_einvoice_not_locked`)
  // so anyone bypassing the UI still gets stopped.
  const irnLocked = (() => {
    if (!isSales) return false;
    const ei = form?.einvoice || {};
    return !!ei.irn && (ei.cancel_status || "") !== "Cancelled";
  })();
  const lockedIrn = (form?.einvoice?.irn || "").slice(0, 16);
  // Tiny post-save lock that keeps the Save button disabled for ~600 ms after
  // a successful submit. Otherwise the form resets so quickly that a user who
  // is double-clicking can immediately fire another POST against the *new*
  // blank draft and end up with a half-populated phantom row (the source of
  // the "duplicate save" report on Purchase Invoice).
  const [justSaved, setJustSaved] = useState(false);
  const [syncingStock, setSyncingStock] = useState(false);

  const [estimateSearch, setEstimateSearch] = useState("");
  const [company, setCompany] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState(""); // "" = All groups
  const [billTypes, setBillTypes] = useState([]);
  const [deductionTypes, setDeductionTypes] = useState([]);
  // Vendor library used by the "Sub Vendor Name" field on Sales / CN / DN
  // forms. Free-text input with a datalist offering Vendor master names
  // plus a fixed sentinel "Radiants (Own Company)" for in-house work.
  const [subVendors, setSubVendors] = useState([]);

  useEffect(() => {
    const branchParam = form.branch_id ? { params: { branch_id: form.branch_id } } : {};
    // One failing master call must never blank the others (Promise.all is
    // all-or-nothing) — each call degrades to an empty list on error.
    const safe = (p, fb = { data: [] }) => p.catch(() => fb);
    const calls = [
      safe(api.get(`/${partnerResource}/picker`, branchParam)),
      safe(api.get("/projects-picker", branchParam)),
      safe(api.get("/items/_pickable")),
      safe(api.get("/company-settings"), { data: null }),
      safe(api.get("/item-groups")),
      safe(api.get("/members/_pickable", branchParam)),
      safe(api.get("/categories")),
    ];
    // Aug-2026 (user report) — /estimates is paginated (200/page); with 200+
    // approved estimates the picker silently missed the rest. Fetch ALL pages.
    const fetchAllEstimates = async (status) => {
      const PAGE = 500;
      let all = [], skip = 0;
      for (;;) {
        const r = await api.get("/estimates", { params: { status, ...(form.branch_id ? { branch_id: form.branch_id } : {}), skip, limit: PAGE } });
        const rows = r.data || [];
        all = all.concat(rows);
        const total = parseInt(r.headers?.["x-total-count"] || "0", 10);
        skip += rows.length;
        if (rows.length < PAGE || (total && skip >= total)) break;
      }
      return all;
    };
    if (isSales) calls.push(safe(Promise.all([
      // JMS statuses are approved-equivalents — a JMS'd estimate stays invoicable
      "Approved", "Partially Invoiced", "Partial JMS Pulled", "Complete JMS pulled", "JMS Completed",
    ].map(fetchAllEstimates)).then((rs) => ({ data: rs.flat() }))));
    Promise.all(calls).then(([p, pr, it, co, grp, mem, cat, est]) => {
      // Edit mode: if the voucher's saved partner is not in the (branch-scoped)
      // picker list, append a fallback entry from the voucher itself so the
      // picker resolves and the required field never blocks a legitimate save.
      const plist = p.data || [];
      const savedPid = invoice?.[`${partnerKey}_id`];
      if (savedPid && !plist.some(x => x.id === savedPid)) {
        plist.push({
          id: savedPid,
          name: invoice[`${partnerKey}_name`] || "(saved partner)",
          [`${partnerKey}_code`]: invoice[`${partnerKey}_code`] || invoice.vendor_code || "",
          gst_number: invoice.gst_number || "",
        });
      }
      // Never downgrade to an empty partner list in edit mode — keep the
      // seeded saved-partner entry if the picker call failed outright.
      setPartners(prev => {
        const merged = plist.length ? plist : (savedPid ? [{
          id: savedPid,
          name: invoice[`${partnerKey}_name`] || "(saved partner)",
          [`${partnerKey}_code`]: invoice[`${partnerKey}_code`] || invoice.vendor_code || "",
          gst_number: invoice.gst_number || "",
        }] : []);
        // Jun-2026 — preserve already-seeded entries (e.g. a pulled-PO vendor
        // registered outside the branch-scoped picker) so the bound vendor
        // always resolves and displays.
        prev.forEach(x => { if (x.id && !merged.some(m => m.id === x.id)) merged.push(x); });
        return merged;
      });
      setProjects(pr.data); setItems(it.data);
      setCompany(co.data);
      setGroups(grp.data || []);
      setMembers(mem.data || []);
      setCategories(Array.isArray(cat.data) ? cat.data : []);
      if (est) setApprovedEstimates(est.data);
    });
    if (!isSales) {
      // Load ALL repair vouchers not yet billed via PI (both open & completed). A vendor
      // can be billed either while the asset is still under repair OR after it returns.
      api.get("/asset-repairs").then(r => {
        const open = (r.data || []).filter(rp => !rp.linked_pi_id);
        setOpenRepairs(open);
      }).catch(() => setOpenRepairs([]));
    }
    if (isSales) {
      api.get("/bill-types").then(r => setBillTypes((r.data || []).filter(b => b.is_active !== false)));
      api.get("/deduction-types").then(r => setDeductionTypes((r.data || []).filter(d => d.is_active !== false)));
      // Vendor library for the "Sub Vendor Name" datalist. Branch-scoped so
      // a Mumbai user sees Mumbai-onboarded vendors first. Tolerant failure
      // — if it errors we still keep the form functional with just the
      // "Radiants (Own Company)" sentinel option.
      api.get("/vendors/picker", branchParam).then(r => setSubVendors(r.data || [])).catch(() => setSubVendors([]));
    }
    if (!invoice && !form.estimate_id) {
      previewNumber(
        isCN ? "sales_credit_note"
        : isDN ? "sales_debit_note"
        : isPureSales ? "sales_invoice"
        : (isPO ? "purchase_order" : "purchase_invoice"),
        new Date().toISOString().slice(0, 10),
      ).then(n => {
        if (n) setForm(f => ({ ...f, invoice_number: f.invoice_number || n, [numberField]: f[numberField] || n }));
      });
    }
    // Load Approved Sales Invoices when creating a Credit/Debit Note.
    // 2026-Feb P1-3 — server-side narrow: only Approved invoices are
    // ever eligible as a "mother invoice" for a CN/DN, so filter at
    // the DB level with `?status=Approved`. Also cap the page-size so
    // long-running tenants don't ship 10k rows to the client for a
    // typeahead. The client-side status re-check is left in place as
    // a belt-and-braces guard against server-side filter changes.
    if (isNote) {
      api.get("/sales-invoices", { params: { status: "Approved", limit: 500 } })
         .then(r => setMotherInvoices((r.data || []).filter(i => i.status === "Approved")));
    }
  }, [partnerResource, isSales, invoice, form.estimate_id, isNote, form.branch_id, mastersRefreshKey]);

  // Jun-2026 — open PPR picker feed (purchase invoices only).
  useEffect(() => {
    if (isSales || isPO || isNote) return;
    api.get("/po-payment-requests/open-picker")
      .then((r) => setOpenPayReqs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSales, isPO, isNote, mastersRefreshKey]);

  // Sprint 4.1 Phase B — Fetch warehouses for the currently-selected
  // branch and auto-select the default WH when no warehouse is yet
  // chosen on the form. SI Stock-Out Phase 2 made the warehouse
  // mandatory on Sales Invoices carrying Inventory lines, so pure
  // sales mode now loads the picker too — only CN / DN skip it
  // (notes never ship from a warehouse).
  useEffect(() => {
    if (isNote) {
      setWarehouses([]);
      return;
    }
    if (!form.branch_id) {
      setWarehouses([]);
      return;
    }
    api.get("/warehouses/picker", { params: { branch_id: form.branch_id } })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setWarehouses(list);
        // Auto-select default warehouse when:
        //  - form has no warehouse yet, OR
        //  - currently selected warehouse no longer exists in this
        //    branch (e.g. branch was switched).
        const cur = list.find(w => w.id === form.warehouse_id);
        if (!cur) {
          const dflt = list.find(w => w.is_default) || list[0];
          if (dflt) {
            setForm(f => ({
              ...f,
              warehouse_id: dflt.id,
              warehouse_name: dflt.warehouse_name,
            }));
          } else {
            setForm(f => ({ ...f, warehouse_id: "", warehouse_name: "" }));
          }
        }
      })
      .catch(() => setWarehouses([]));
  // form.warehouse_id intentionally omitted to avoid loop — we manage it
  // ourselves above when branch changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNote, form.branch_id]);

  // Sprint 2026-Q1 (round 3) — Purchase Order: default the shipping
  // address to the active branch's address as soon as the operator
  // picks (or auto-inherits) a branch. Vendors ship goods to OUR
  // office by default; the operator can edit the textarea on the
  // form if a different ship-to applies. We only auto-fill when the
  // field is empty so we never overwrite a manual entry.
  useEffect(() => {
    if (!isPO) return;
    if (!form.branch_id) return;
    if ((form.shipping_address || "").trim()) return;  // operator edited — respect it
    const br = (branches || []).find(b => b.id === form.branch_id);
    if (!br) return;
    const lines = [
      br.legal_name || br.branch_name || "",
      br.address_line1 || br.address || "",
      br.address_line2 || "",
      [br.city, br.state, br.pincode].filter(Boolean).join(", "),
    ].map(s => (s || "").trim()).filter(Boolean);
    if (!lines.length) return;
    const defaultShip = lines.join("\n");
    setForm(f => ({ ...f, shipping_address: defaultShip }));
  // shipping_address intentionally omitted — we only seed once when empty.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPO, form.branch_id, branches]);

  // Items filtered by selected Group (empty = show all)
  // Match by group_id when set, else case-insensitive group_name. Catches
  // items added via Excel bulk upload or saved before being linked to the
  // Item Group library (legacy rows store only the free-text label).
  function _itemInGroup(it, g) {
    if (!g) return false;
    if ((it.group_id || "") && it.group_id === g.id) return true;
    const inm = (it.group_name || "").trim().toLowerCase();
    const gnm = (g.name || "").trim().toLowerCase();
    return !!inm && inm === gnm;
  }
  const filteredItems = groupFilter
    ? items.filter(it => _itemInGroup(it, groups.find(g => g.id === groupFilter)))
    : items;

  // Derive the search-picker text for a line: prefer explicit _picker_label
  // (typed/cleared by the user), else compute from item_id when in edit/pull mode.
  function pickerLabelFor(l) {
    if (l._picker_label !== undefined) return l._picker_label;
    if (!l.item_id) return "";
    const it = items.find(x => x.id === l.item_id);
    if (!it) return "";
    return `${it.item_code ? `${it.item_code} · ` : ""}${it.name}${it.item_type ? ` · ${it.item_type}` : ""}`;
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setLine(idx, k, v) {
    const lines = [...form.line_items];
    lines[idx] = { ...lines[idx], [k]: v };
    const ln = lines[idx];
    // Recompute effective rate whenever base/discount inputs change
    const recomputeRate = (b => {
      if (isSales) {
        const mode = ln.management_fee_type || "none";
        const val = Number(ln.management_fee_value || 0);
        const fee = mode === "percent" ? (b * val) / 100 : mode === "amount" ? val : 0;
        ln.management_fee_amount = Number(fee.toFixed(2));
        ln.rate = Number((b + fee).toFixed(2));
      } else {
        const mode = ln.purchase_discount_type || "none";
        const val = Number(ln.purchase_discount_value || 0);
        const disc = mode === "percent" ? (b * val) / 100 : mode === "amount" ? val : 0;
        ln.purchase_discount_amount = Number(disc.toFixed(2));
        ln.rate = Number((b - disc).toFixed(2));
      }
    });
    if (k === "base_rate") {
      recomputeRate(Number(ln.base_rate || 0));
    }
    if (!isSales && k === "purchase_discount_value") {
      // User changed Disc % directly → switch to percent mode and recompute
      ln.purchase_discount_type = "percent";
      recomputeRate(Number(ln.base_rate || ln.rate || 0));
    }
    if (!isSales && k === "purchase_discount_amount") {
      // User overrode Disc Amt directly → switch to amount mode
      const base = Number(ln.base_rate || ln.rate || 0);
      const disc = Number(v || 0);
      ln.purchase_discount_type = "amount";
      ln.purchase_discount_value = disc;
      ln.purchase_discount_amount = disc;
      ln.rate = Number((base - disc).toFixed(2));
    }
    if (k === "quantity" || k === "rate" || k === "base_rate"
        || (!isSales && (k === "purchase_discount_value" || k === "purchase_discount_amount"))) {
      ln.amount = Number(Number(ln.quantity || 0) * Number(ln.rate || 0));
    }
    lines[idx] = ln;
    setForm({ ...form, line_items: lines });
  }
  function pickItem(idx, itemId) {
    const it = items.find(x => x.id === itemId);
    const lines = [...form.line_items];
    if (it) {
      // Compute effective rate based on PI vs SI side.
      // PI → base purchase_rate minus discount.
      // SI → base sales_rate plus management fee.
      let effRate = 0;
      let extra = {};
      if (isSales) {
        const base = Number(it.sales_rate ?? it.rate ?? 0);
        const mode = it.management_fee_type || "none";
        const val = Number(it.management_fee_value || 0);
        const fee = mode === "percent" ? (base * val) / 100 : mode === "amount" ? val : 0;
        effRate = base + fee;
        extra = {
          base_rate: base,
          management_fee_type: mode,
          management_fee_value: val,
          management_fee_amount: Number(fee.toFixed(2)),
        };
      } else {
        const base = Number(it.purchase_rate ?? 0);
        const mode = it.purchase_discount_type || "none";
        const val = Number(it.purchase_discount_value || 0);
        const disc = mode === "percent" ? (base * val) / 100 : mode === "amount" ? val : 0;
        effRate = base - disc;
        extra = {
          base_rate: base,
          purchase_discount_type: mode,
          purchase_discount_value: val,
          purchase_discount_amount: Number(disc.toFixed(2)),
        };
      }
      const qty = Number(lines[idx].quantity || 0);
      lines[idx] = {
        ...lines[idx], item_id: it.id, item_code: it.item_code, description: it.name,
        unit: it.unit || "NOS", rate: Number(effRate.toFixed(2)), gst_rate: Number(it.gst_rate || 18),
        amount: Number((qty * effRate).toFixed(2)),
        item_type: it.item_type || "Inventory",
        rc_line_item_no: it.rc_line_item_no || "",
        type: it.type || "",
        // Carry Brand / Warranty (and leave Order Number / Order Month / Client Server blank for user entry)
        brand: lines[idx].brand || it.brand || "",
        warranty: lines[idx].warranty || it.warranty || "",
        order_number: lines[idx].order_number || "",
        order_month: lines[idx].order_month || "",
        client_server: lines[idx].client_server || "",
        _picker_label: `${it.item_code ? `${it.item_code} · ` : ""}${it.name}${it.item_type ? ` · ${it.item_type}` : ""}`,
        ...extra,
      };
    } else {
      lines[idx] = { ...lines[idx], item_id: "", item_code: "", _picker_label: "" };
    }
    setForm({ ...form, line_items: lines });
  }
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { ...emptyLine }] });
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  // Aug-2026 — "JMS Quick Pull": open a Completed JMS → New Sales Invoice
  // with its estimate pulled (remaining qty) and JMS number pre-filled.
  useEffect(() => {
    if (!prefilled || typeof prefilled !== "object" || prefilled.kind !== "FROM_JMS") return;
    const j = prefilled.jms || {};
    if (!j.estimate_id) { toast.error("This JMS has no linked estimate"); return; }
    (async () => {
      try {
        const { data: est } = await api.get(`/estimates/${j.estimate_id}`);
        pullEstimate(est, {
          jms_number: j.jms_number || "",
          service_period: j.service_period || est.service_period || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load the JMS estimate");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilled]);

  function pullEstimate(est, extraForm = {}) {
    const remainingLines = (est.line_items || [])
      .map((l, idx) => {
        const max = Number(l.quantity || 0) - Number(l.invoiced_quantity || 0);
        return { ...l, _source_index: idx, _max_quantity: max,
          quantity: max, amount: max * Number(l.rate || 0) };
      })
      .filter(l => l._max_quantity > 0.0001);
    if (remainingLines.length === 0) {
      toast.error("This estimate has no remaining quantity to invoice");
      return;
    }
    // Phase C-Lite smart default: SES/Work-Completion month → parseable
    // Service Period → keep the current (invoice-month) default.
    const rpDerived = monthFromDate(est.work_completion_date) || parsePeriodText(est.service_period);
    if (rpDerived) rpTouchedRef.current = true;
    setForm({
      ...form,
      client_id: est.client_id, project_id: est.project_id,
      sales_gst_type: est.sales_gst_type, document_type_credit: est.document_type_credit,
      location: est.location, category: est.category, warehouse: est.warehouse,
      email: est.email, currency: est.currency, conversion_rate: est.conversion_rate,
      salesman: est.salesman, service_period: est.service_period || "", agent: est.agent, tax_treatment: est.tax_treatment,
      reporting_period: rpDerived || form.reporting_period || monthFromDate(form.invoice_date),
      payment_terms: est.payment_terms, due_date: est.valid_until,
      ref_no: est.estimate_number, ref_date: est.estimate_date,
      rc_number: est.rc_number || "",
      billing_address: est.billing_address, shipping_address: est.shipping_address,
      gst_number: est.gst_number, estimate_id: est.id, estimate_number: est.estimate_number,
      line_items: remainingLines, tds_amount: 0,
      ...extraForm,
    });
    setEstimatePicker(false);
    toast.success(`Pulled ${remainingLines.length} line(s) from ${est.estimate_number}`);
  }

  const onPartnerChange = (id) => {
    const p = partners.find(x => x.id === id);
    // For Clients (Sales side), prefer billing_address & default shipping_address.
    const ship = isSales && p?.shipping_addresses?.length
      ? (p.shipping_addresses.find(s => s.is_default) || p.shipping_addresses[0])
      : null;
    const shipStr = ship ? [ship.label, ship.address_line1, ship.address_line2, [ship.city, ship.state, ship.pincode].filter(Boolean).join(", ")].filter(Boolean).join("\n") : "";
    // For Purchase: auto-compute TDS from vendor.tds_rate on the current taxable subtotal.
    const next = {
      ...form, [`${partnerKey}_id`]: id,
      email: form.email || p?.email || "",
      billing_address: form.billing_address || p?.billing_address || p?.address || "",
      shipping_address: form.shipping_address || shipStr || p?.address || "",
      shipping_address_label: ship ? ship.label : "",
      gst_number: form.gst_number || p?.gst_number || "",
    };
    // Client change → reset the RC Number so a stale contract number
    // from the previous customer's project doesn't survive. The dropdown
    // re-derives its option list from the new customer's active projects
    // and the operator picks a fresh value.
    if (isSales) next.rc_number = "";
    if (!isSales && p && !form.tds_user_edited) {
      const sub = form.line_items.reduce((s, l) => s + Number(l.amount || 0), 0);
      const rate = Number(p.tds_rate || 0);
      next.tds_amount = +(sub * rate / 100).toFixed(2);
      next.tds_rate_applied = rate;
    }
    setForm(next);
  };

  // For shipping address picker on Sales side
  const activePartner = partners.find(x => x.id === form[`${partnerKey}_id`]);
  const shippingOptions = isSales ? (activePartner?.shipping_addresses || []) : [];
  function pickShippingAddress(idx) {
    const s = shippingOptions[idx];
    if (!s) return;
    const text = [s.address_line1, s.address_line2, [s.city, s.state, s.pincode].filter(Boolean).join(", "),
                  s.contact_person ? `Contact: ${s.contact_person} ${s.contact_phone || ""}`.trim() : ""].filter(Boolean).join("\n");
    setForm({ ...form, shipping_address: text, shipping_address_label: s.label || "" });
  }

  const isTaxable = (form.sales_gst_type || "Tax Invoice") === "Tax Invoice";
  const subtotal = form.line_items.reduce((s, l) => s + Number(l.amount || 0), 0);
  const tax = isTaxable ? form.line_items.reduce((s, l) => s + Number(l.amount || 0) * Number(l.gst_rate || 0) / 100, 0) : 0;

  // Auto-recompute TDS when the taxable subtotal changes for Purchase Invoice,
  // using the vendor's master tds_rate. User can override and we stop touching.
  useEffect(() => {
    if (isSales) return;
    if (form.tds_user_edited) return;
    const vid = form[`${partnerKey}_id`];
    if (!vid) return;
    const ven = partners.find(p => p.id === vid);
    if (!ven) return;
    const rate = Number(ven.tds_rate || 0);
    const newTds = +(subtotal * rate / 100).toFixed(2);
    if (Math.abs(newTds - Number(form.tds_amount || 0)) > 0.001 || form.tds_rate_applied !== rate) {
      setForm(f => ({ ...f, tds_amount: newTds, tds_rate_applied: rate }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, form[`${partnerKey}_id`], isSales]);

  // GST bifurcation: intra-state → CGST + SGST equally; inter-state → IGST.
  // Seller state resolution chain: selected Branch.state_code → Branch.gst_number[:2]
  // → CompanySettings.state_code → CompanySettings.gstin[:2]. The Company-level
  // fallback handles the common case where a branch (e.g. Head Office) is created
  // without filling in its own GSTIN.
  const selectedBranch = branches.find(b => b.id === form.branch_id);
  const sellerState =
    ((selectedBranch?.state_code || "").slice(0, 2)) ||
    ((selectedBranch?.gst_number || "").slice(0, 2)) ||
    ((company?.state_code || "").slice(0, 2)) ||
    ((company?.gstin || "").slice(0, 2));
  const buyerState = (form.gst_number || "").slice(0, 2);
  const isIntraState = isTaxable && !!sellerState && !!buyerState && sellerState === buyerState;
  // Section 8, CGST Act: CGST MUST equal SGST on intra-state supplies.
  // Compute the half ONCE with round-half-up (the rounding our backend uses
  // for the source-of-truth `_apply_totals`). The naive
  //   cgst = round(tax/2); sgst = round(tax - cgst)
  // pattern splits odd-paisa totals unevenly (e.g. ₹2670.71 → 1335.36 + 1335.35)
  // and any user who edits an invoice would see the asymmetric values in the
  // totals panel — that's the bug reported on the live server.
  const roundHalfUp2 = (n) => {
    // Math.round in JS rounds 0.5 toward +Infinity which matches HALF_UP for
    // positive numbers — but float precision can still bite (e.g.
    // 1335.355 stored as 1335.3549999...). We add a tiny epsilon-safe nudge
    // by scaling to integer paisa first.
    return Math.round((n + Number.EPSILON) * 100) / 100;
  };
  const half = isIntraState ? roundHalfUp2(tax / 2) : 0;
  const cgst = half;
  const sgst = half;        // ENFORCED equal to CGST
  // Adjust tax for display so the panel reconciles (cgst + sgst = displayed tax).
  const taxDisplay = isIntraState ? roundHalfUp2(half * 2) : roundHalfUp2(tax);
  const igst = isTaxable && !isIntraState ? roundHalfUp2(tax) : 0;

  const rawTotal = subtotal + taxDisplay;
  const total = form.enable_round_up ? Math.round(rawTotal) : rawTotal;
  const roundAdj = form.enable_round_up ? +(Math.round(rawTotal) - rawTotal).toFixed(2) : 0;
  const net = total - Number(form.tds_amount || 0);

  async function save(e) {
    e.preventDefault();
    if (!form.project_id) { toast.error("Project is mandatory"); return; }
    if (!form[`${partnerKey}_id`]) { toast.error(`${isSales ? "Client" : "Vendor"} is required`); return; }
    if (!form.branch_id) { toast.error("Branch is required"); return; }
    // Sprint 4.1 Phase B — Warehouse mandatory on Purchase Invoice / PO.
    // Direct Ops POs skip this: receiving happens through Project GRN into
    // project stock, not a warehouse.
    if (!isSales && !form.warehouse_id && !opsDirectCreate) {
      toast.error("Warehouse is required. Pick a warehouse for the selected branch.");
      return;
    }
    // Sprint 2026-Feb / SI Stock-Out Phase 2 — Warehouse mandatory on
    // Sales Invoice whenever at least one line carries an Inventory
    // item. Service-only + Asset-only SIs remain warehouse-optional.
    if (isPureSales && !form.warehouse_id) {
      const hasInventoryLine = (form.line_items || []).some(
        l => (l.item_type || "").trim() === "Inventory"
             && (l.item_id || "").trim()
             && Number(l.quantity || 0) > 0
      );
      if (hasInventoryLine) {
        toast.error(
          "Warehouse is required for Sales Invoices that contain Inventory items. "
          + "Pick a warehouse for the selected branch."
        );
        return;
      }
    }
    if (isSales && !(form.ref_no || "").trim()) { toast.error("Ref No is mandatory."); return; }
    if (!isSales && !isPO && !(form.ref_no || "").trim()) { toast.error("Vendor Ref / Invoice No is mandatory."); return; }
    // Validate pull-from-estimate caps
    if (form.estimate_id && isSales && !invoice) {
      for (const l of form.line_items) {
        if (l._max_quantity !== undefined && Number(l.quantity || 0) > Number(l._max_quantity) + 0.001) {
          toast.error(`Line "${l.description}": qty ${l.quantity} exceeds available ${l._max_quantity}`);
          return;
        }
      }
    }
    setSaving(true);
    const partner = partners.find(p => p.id === form[`${partnerKey}_id`]);
    const project = projects.find(p => p.id === form.project_id);
    // Strip server-managed e-invoice block — backend is the source of
    // truth for IRN / Ack / QR / sync state. Re-sending these on PUT is
    // a no-op at best and can trip the Sprint 3.11 IRN-locked guard at
    // worst (dict-comparison false positive on nested objects).
    const { einvoice: _ei, ...formNoEi } = form;
    let payload = {
      ...formNoEi,
      [`${partnerKey}_name`]: partner?.name || "",
      project_name: project?.project_name || "",
      tds_amount: Number(form.tds_amount || 0),
      conversion_rate: Number(form.conversion_rate || 1),
      line_items: form.line_items.map(l => {
        const { _source_index, _max_quantity, _picker_label, _remaining_quantity, _expanded, ...rest } = l;
        return {
          ...rest, quantity: Number(l.quantity || 0), rate: Number(l.rate || 0),
          gst_rate: Number(l.gst_rate || 0), amount: Number(l.amount || 0),
          pack_qty: Number(l.pack_qty || 1),
        };
      }),
    };
    // Sprint 3.11 — When the SI carries a live IRN, the backend rejects
    // ANY change to financial / party / line_items fields. The UI builds
    // a SLIM payload containing only the editable free-text fields so we
    // never trip the guard while saving operator-facing corrections.
    if (irnLocked && invoice) {
      const editableKeys = [
        "jms_number",
        "alternate_order_no", "alternate_order_no_date",
        "ref_no", "ref_date", "rc_number",
        "shipping_address",
        "service_period",
        "payment_terms", "due_date",
        "work_order_no",
        "transport_mode", "vehicle_no", "vehicle_type",
        "transporter_name", "transporter_id",
        "lr_no", "lr_date", "distance_km",
        "dispatch_from", "delivery_to", "sub_supply_type",
        "generate_eway", "eway_bill_no",
        "salesman", "agent", "sub_vendor_name",
        "location", "category", "warehouse", "email",
        "remarks",
        "attachments", "attachment",
        // Jun-2026 — TDS is NOT part of the e-invoice financial block on
        // the IRP, so the tick/rate stays editable even when IRN-locked.
        // Server recomputes tds_amount + net_receivable from these.
        "enable_tds", "tds_rate",
      ];
      const slim = {};
      for (const k of editableKeys) {
        if (form[k] !== undefined) slim[k] = form[k];
      }
      payload = slim;
    }
    // Sales-side: pass only ENABLED special_deductions with valid name + amount > 0
    // Skip when the IRN-locked slim payload path is in effect.
    if (isSales && !(irnLocked && invoice)) {
      payload.special_deductions = (form.special_deductions || [])
        .filter(d => d.enabled && (d.name || "").trim() && Number(d.amount || 0) > 0)
        .map(d => ({ name: d.name.trim(), amount: Number(d.amount) }));
    }
    // For Purchase Order mode, the backend expects po_number / po_date — remap.
    if (isPO) {
      payload.po_number = form.invoice_number || "";
      payload.po_date = form.invoice_date || "";
      delete payload.invoice_number;
      delete payload.invoice_date;
    }
    // For Credit / Debit Note mode, the backend expects note_number / note_date.
    if (isNote) {
      payload.note_number = form.invoice_number || form.note_number || "";
      payload.note_date = form.invoice_date || form.note_date || "";
      delete payload.invoice_number;
      delete payload.invoice_date;
    }
    try {
      if (opsDirectCreate) {
        if (!directOps.projects.some(p => p.id === form.project_id)) {
          toast.error("Pick an Operational Project (Project Operations enabled) for a Direct Ops PO");
          setSaving(false);
          return;
        }
        const r = await api.post(`/projects/${form.project_id}/direct-ops-po`, payload);
        toast.success(`Draft Ops PO ${r.data.po_number} created — open it from the PO list to submit for approval`);
      } else if (form.estimate_id && isSales && !invoice) {
        // Partial pull through convert-to-invoice. Only send line_quantities for
        // rows that came from the estimate (have _source_index). Custom rows added
        // by the user are ignored here since the backend only emits estimate-derived
        // lines on convert.
        payload.line_quantities = form.line_items
          .filter(l => l._source_index !== undefined && l._source_index !== null)
          .map(l => ({ index: Number(l._source_index), quantity: Number(l.quantity || 0) }));
        await api.post(`/estimates/${form.estimate_id}/convert-to-invoice`, payload);
      } else if (invoice) {
        await api.put(`/${resource}/${invoice.id}`, payload);
      } else {
        await api.post(`/${resource}`, payload);
      }
      if (!opsDirectCreate) toast.success(invoice ? `${docNoun} updated — close the tab when done` : `${docNoun} created — start the next one below`);
      onSaved();
      // For a new voucher, reset the form to a blank draft so the user can
      // key the next entry without re-opening the tab. For an edit we leave
      // the form mounted with the updated values so the user can see their
      // change land — they close the tab via the X button.
      if (!invoice) {
        setForm(blankDraft());
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* old browsers */ }
      }
      // Brief post-save lock so a double-click can't immediately re-POST the
      // half-cleared form. Release after the form has visibly cleared.
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  // Pull-from-estimate picker overlay
  if (estimatePicker) {
    const q = estimateSearch.trim().toLowerCase();
    const filteredEstimates = !q ? approvedEstimates : approvedEstimates.filter(e =>
      (e.estimate_number || "").toLowerCase().includes(q) ||
      (e.client_name || "").toLowerCase().includes(q) ||
      (e.project_name || "").toLowerCase().includes(q) ||
      (e.salesman || "").toLowerCase().includes(q) ||
      (e.ref_no || "").toLowerCase().includes(q)
    );
    return (
      <ViewOverlay testid="estimate-picker-overlay" className="overflow-hidden flex flex-col">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between bg-white shadow-sm">
          <h2 className="font-display text-2xl font-bold">Pull from Approved Estimate</h2>
          <button type="button" onClick={() => setEstimatePicker(false)} className="bg-white border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50" data-testid="picker-skip-btn">
            Skip — Create Blank Invoice
          </button>
        </div>
        <div className="px-8 py-4 border-b border-slate-100 bg-slate-50">
          <div className="max-w-[1600px] mx-auto">
            <input data-testid="estimate-search" autoFocus value={estimateSearch}
              onChange={e => setEstimateSearch(e.target.value)}
              placeholder="Search by estimate no, client, project, salesman, ref no…"
              className="w-full border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#F97316]" />
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">
              {filteredEstimates.length} of {approvedEstimates.length} {approvedEstimates.length === 1 ? "estimate" : "estimates"}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1600px] mx-auto px-8 py-2">
            {approvedEstimates.length === 0 ? <div className="p-10 text-center text-slate-500">No estimates available with remaining qty. Approve a quotation first.</div> : filteredEstimates.length === 0 ? <div className="p-10 text-center text-slate-500">No estimates match &quot;{estimateSearch}&quot;.</div> : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>{["Estimate No", "Date", "Status", "Client", "Project", "Remaining Value", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {filteredEstimates.map(e => {
                    const remVal = (e.line_items || []).reduce((s, l) => {
                      const r = Number(l.quantity || 0) - Number(l.invoiced_quantity || 0);
                      return s + (r > 0 ? r * Number(l.rate || 0) : 0);
                    }, 0);
                    return (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-xs font-bold">{e.estimate_number}</td>
                      <td className="py-3 px-4 text-xs">{formatDate(e.estimate_date)}</td>
                      <td className="py-3 px-4"><StatusBadge status={e.status} /></td>
                      <td className="py-3 px-4 text-sm">{e.client_name}</td>
                      <td className="py-3 px-4 text-sm">{e.project_name}</td>
                      <td className="py-3 px-4 text-sm tabular font-bold text-[#F97316]">{formatINR(remVal)}</td>
                      <td className="py-3 px-4"><button data-testid={`pick-est-${e.id}`} onClick={() => pullEstimate(e)} className="bg-[#F97316] text-white px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold hover:bg-[#EA580C]">Pull →</button></td>
                    </tr>);
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="px-8 py-4 border-t border-slate-200 flex justify-end bg-white">
          <button onClick={() => setEstimatePicker(false)} className="text-xs uppercase tracking-wider text-slate-600 hover:text-slate-900 font-bold">Skip / Create blank invoice</button>
        </div>
      </ViewOverlay>
    );
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="invoice-form-overlay">
      <div className="bg-white w-full min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div>
            <h2 className="font-display text-2xl font-bold">{invoice ? "Edit" : "New"} {isCN ? "Sales Credit Note" : isDN ? "Sales Debit Note" : isPureSales ? "Sales Invoice" : (isPO ? "Purchase Order" : "Purchase Invoice")}</h2>
            {form.estimate_number && <div className="text-xs text-[#F97316] font-bold mt-1">← Pulled from Estimate {form.estimate_number}</div>}
            {form._pulled_from_po && <div className="text-xs text-[#F97316] font-bold mt-1">← Pulled from PO {form._pulled_from_po}</div>}
            {isPO && invoice?.procurement_flow === "PROJECT_OPERATIONS" && (
              <div className="mt-1" data-testid="projops-po-banner">
                <div className="text-xs">
                  <span className="inline-block bg-sky-100 border border-sky-300 text-sky-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider mr-1.5">
                    Project Operations{invoice.status === "Approved" ? " · Approved" : invoice.status === "Partially Invoiced" ? " · Partially Invoiced" : invoice.status === "Pending" ? " · Pending Approval" : invoice.status === "Rejected" ? " · Rejected" : invoice.status === "Cancelled" ? " · Cancelled" : " Draft"}
                  </span>
                  <span className="text-slate-600 font-bold">{invoice.po_origin === "DIRECT" ? "Direct Ops PO — raised without a PR · Project locked" : invoice.po_origin === "BOQ_DIRECT" ? "BOQ Vendor PO — raised from the Approved BOQ without a PR · Project locked" : `← Converted from PR ${invoice.source_pr_number || ""} · Project locked`}</span>
                </div>
                <BoqPoDeductionNote po={invoice} />
                {invoice.source_pr_id && (
                  <PrAttachmentsStrip projectId={invoice.project_id} prId={invoice.source_pr_id} />
                )}
                {["Approved", "Partially Invoiced"].includes(invoice.status) ? (
                  <div className="text-[11px] mt-0.5 space-y-0.5">
                    <div className="text-emerald-700 font-bold" data-testid="projops-grn-pending-msg">
                      PO approved{Number(invoice.po_approval_version || 0) > 1 ? ` (amendment v${invoice.po_approval_version})` : ""} — {invoice.grn_not_required ? "service items, no GRN needed; PI can be raised directly against the approved lines." : "GRN receiving and PI pulls run against the approved lines."}
                    </div>
                    {projOpsApprovalOn ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <button type="button" data-testid="projops-po-amend-btn" disabled={submittingPo}
                          onClick={amendProjOpsPo}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold disabled:opacity-60">
                          {submittingPo ? "Working…" : "Amend PO"}
                        </button>
                        <span className="text-amber-700 font-bold" data-testid="projops-amend-msg">
                          Returns the PO to Draft for editing, then it follows the approval matrix again. Quantities on GRN-received lines can only be increased; GRN / new PI pulls pause until re-approval.
                        </span>
                      </div>
                    ) : (
                      <div className="text-amber-700 font-bold" data-testid="projops-amend-msg">
                        PO amendments require the Project Operations PO approval control to be enabled.
                      </div>
                    )}
                  </div>
                ) : invoice.status === "Pending" ? (
                  <div className="text-[11px] text-amber-700 font-bold mt-0.5" data-testid="projops-pending-msg">
                    Pending PO approval — editing is disabled until an approver acts on it in the Approvals Inbox.
                  </div>
                ) : invoice.status === "Cancelled" ? (
                  <div className="text-[11px] text-red-700 font-bold mt-0.5" data-testid="projops-cancelled-msg">
                    Cancelled{invoice.cancellation_reason ? ` — ${invoice.cancellation_reason}` : ""}. Routing snapshots are retained for audit.
                  </div>
                ) : (
                  <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap">
                    {invoice.status === "Rejected" && (
                      <span className="text-red-700 font-bold" data-testid="projops-rejected-msg">
                        Rejected{invoice.rejection_reason ? ` — ${invoice.rejection_reason}` : ""}. Edit and resubmit.
                      </span>
                    )}
                    {["DIRECT", "BOQ_DIRECT"].includes(invoice.po_origin) && (
                      <button type="button" data-testid="projops-direct-cancel-btn" disabled={submittingPo}
                        onClick={cancelDirectOpsPo}
                        className="bg-white border border-red-300 text-red-700 hover:bg-red-50 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold disabled:opacity-60">
                        Cancel Draft
                      </button>
                    )}
                    {projOpsApprovalOn ? (
                      <>
                        <span className="text-slate-700 font-bold">Ready — submit through the existing PO Approval Matrix.</span>
                        <button type="button" data-testid="projops-po-submit-btn" disabled={submittingPo}
                          onClick={submitProjOpsPo}
                          className="bg-[#F97316] hover:bg-[#EA580C] text-white px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold disabled:opacity-60">
                          {submittingPo ? "Submitting…" : "Submit for Approval"}
                        </button>
                      </>
                    ) : (
                      <span className="text-amber-700 font-bold">
                        Project Procurement PO approval will become available after the Project GRN receiving control is activated.
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isPureSales && !invoice && !form.estimate_id && (
              <button type="button" data-testid="pull-from-estimate-btn" onClick={() => setEstimatePicker(true)}
                className="bg-[#1A1D21] hover:bg-[#000] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
                <FileCheck2 size={14} /> Pull from Estimate
              </button>
            )}
            {!isSales && !isPO && !invoice && (
              <button type="button" data-testid="pull-from-po-btn" onClick={() => setPoPickerOpen(true)}
                className="bg-[#1A1D21] hover:bg-[#000] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
                <FileCheck2 size={14} /> Pull from Purchase Order
              </button>
            )}
            <button type="button" data-testid="view-invoice-list-btn" onClick={onViewList || onClose}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              View List
            </button>
            <RefreshMastersButton testid="invoice-refresh-masters-btn" />
          </div>
        </div>
        <form onSubmit={save} className="px-8 py-6 space-y-6 max-w-[1600px] mx-auto">
          {irnLocked && (
            <div data-testid="irn-locked-banner"
              className="bg-amber-50 border-l-4 border-amber-500 px-5 py-4 flex items-start gap-3 mb-2">
              <Lock size={18} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-amber-900">
                  E-Invoice generated · Financial & party blocks are locked
                </div>
                <div className="text-[13px] text-amber-900 mt-1 leading-relaxed">
                  IRN <span className="font-mono font-bold">{lockedIrn}…</span> is live on the GST IRP.
                  GST rules forbid changes to amounts, taxes, line items, customer, project, branch
                  or invoice identity once an IRN is issued. Free-text fields (<strong>JMS Number,
                  alternate order, transport, attachments, shipping address text, remarks</strong>)
                  remain editable so you can still produce a clean PDF.
                </div>
                <ul className="text-[12px] text-amber-900 mt-2 space-y-0.5 list-disc list-inside">
                  <li>To change financial or party fields: open the <strong>GST Centre · Generated</strong> tab
                    and <strong>Cancel IRN</strong> (allowed within 24h of generation). The invoice
                    will reopen fully for edits.</li>
                  <li>For a commercial adjustment after the 24h window, raise a <strong>Credit Note</strong>
                    or <strong>Debit Note</strong> against this invoice.</li>
                </ul>
              </div>
            </div>
          )}

          {isPO && invoice?.id && <VoucherScanLog vtype="po" vid={invoice.id} />}

          {isProjOpsPo && invoice?.status === "Approved" && (
            <ProjOpsRoutingSummary po={invoice} />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-1 pb-4 border-b border-slate-200">
            {/* COLUMN 1 — Document classification & Customer */}
            <div className="space-y-1">
              <CompactField label="Category">
                <input
                  data-testid="inv-category"
                  list="inv-category-list"
                  value={form.category}
                  onChange={e => {
                    const v = e.target.value;
                    set("category", v);
                    if (v !== "Asset Repair") {
                      set("linked_repair_id", "");
                      set("linked_repair_number", "");
                    }
                  }}
                  placeholder={!isSales ? "Type or pick (e.g. Asset Repair)" : "Pick or type new (e.g. Lakme / Schneider)"}
                  className={compactInput}
                />
                <datalist id="inv-category-list">
                  {!isSales && <option value="Asset Repair" />}
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </CompactField>
              {!isSales && form.category === "Asset Repair" && (
                <CompactField label="Repair Request No *" required>
                  {(() => {
                    const rep = openRepairs.find(r => r.id === form.linked_repair_id);
                    const rrnLabel = (r) => `${r.voucher_number} · ${r.asset_code} · ${r.vendor_name || ""} · ${r.repair_completed_at ? "Received" : "In-Repair"}`;
                    const boundLabel = rep ? rrnLabel(rep) : (form.linked_repair_number || "");
                    const displayValue = rrnSearch !== null ? rrnSearch : boundLabel;
                    return (
                      <>
                        <input data-testid="inv-rrn" list="inv-rrn-list" required
                          value={displayValue}
                          placeholder={`🔍 Search ${openRepairs.length} unbilled repair${openRepairs.length === 1 ? "" : "s"} (incl. completed) by RRN / asset / vendor…`}
                          onChange={e => {
                            const raw = e.target.value || "";
                            setRrnSearch(raw);
                            const v = raw.trim().toLowerCase();
                            const m = openRepairs.find(r => rrnLabel(r).toLowerCase() === v);
                            if (m) {
                              set("linked_repair_id", m.id);
                              set("linked_repair_number", m.voucher_number);
                              // Auto-fill vendor from the repair voucher
                              if (m.vendor_id && !form.vendor_id) {
                                set("vendor_id", m.vendor_id);
                                set("vendor_name", m.vendor_name);
                              }
                              setRrnSearch(null);
                            } else if (!v) {
                              set("linked_repair_id", "");
                              set("linked_repair_number", "");
                            }
                          }}
                          onBlur={() => setRrnSearch(null)}
                          className={compactInput} />
                        <datalist id="inv-rrn-list">
                          {openRepairs.map(r => (
                            <option key={r.id} value={rrnLabel(r)} />
                          ))}
                        </datalist>
                        {form.linked_repair_id && (
                          <div className="text-[10px] text-emerald-700 mt-0.5">✓ Linked to RRN {form.linked_repair_number}</div>
                        )}
                      </>
                    );
                  })()}
                </CompactField>
              )}
              <CompactField label="GST Type">
                <select data-testid="inv-gst-type" value={form.sales_gst_type} onChange={e => set("sales_gst_type", e.target.value)} className={compactInput}>
                  {["Tax Invoice", "Bill of Supply - Non GST"].map(s => <option key={s}>{s}</option>)}
                </select>
              </CompactField>
              {isNote && (
                <>
                  <CompactField label={`Mother Invoice No * (${isCN ? "issue Credit Note against" : "issue Debit Note against"})`} required>
                    <input
                      type="text"
                      data-testid={`${isCN ? "cn" : "dn"}-mother-invoice`}
                      required
                      value={form.reference_invoice_number || ""}
                      onChange={e => set("reference_invoice_number", e.target.value)}
                      placeholder="e.g. RCPL/0123/26-27"
                      className={compactInput}
                    />
                  </CompactField>
                  {/* Mother Invoice Date — pulled into the PDF as "Ag Invoice
                      Date" right next to "Ag Invoice No". Helps auditors trace
                      the CN/DN to the original SI at a glance. Optional but
                      strongly recommended for proper paper trails. */}
                  <CompactField label="Mother Invoice Date">
                    <input
                      type="date"
                      data-testid={`${isCN ? "cn" : "dn"}-mother-invoice-date`}
                      value={(form.reference_invoice_date || "").slice(0, 10)}
                      onChange={e => set("reference_invoice_date", e.target.value)}
                      className={compactInput}
                    />
                  </CompactField>
                  <CompactField label="Reason *" required>
                    <select data-testid={`${isCN ? "cn" : "dn"}-reason`} required value={form.reason || ""} onChange={e => set("reason", e.target.value)} className={compactInput}>
                      <option value="">— Select —</option>
                      {(isCN
                        ? ["Returns", "Discount", "Price Revision", "Quality Issue", "Cancellation", "Other"]
                        : ["Undercharge", "Additional Supply", "Price Increase", "Interest / Late Fee", "Other"]
                      ).map(r => <option key={r}>{r}</option>)}
                    </select>
                  </CompactField>
                </>
              )}
              {isSales && (
                <CompactField label="Bill Type">
                  <select data-testid="inv-bill-type" value={form.bill_type || "Credit"}
                    onChange={async e => {
                      const v = e.target.value;
                      if (v === "__add_new__") {
                        const name = window.prompt("Enter new Bill Type (e.g. Milestone, Advance Adjusted):");
                        if (name && name.trim()) {
                          try {
                            const r = await api.post("/bill-types", { name: name.trim() });
                            const refreshed = await api.get("/bill-types");
                            setBillTypes((refreshed.data || []).filter(b => b.is_active !== false));
                            set("bill_type", r.data.name);
                            toast.success(`Added "${r.data.name}"`);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Could not add bill type");
                          }
                        }
                      } else {
                        set("bill_type", v);
                      }
                    }} className={compactInput}>
                    {billTypes.length === 0
                      ? ["Cash", "Credit", "Advance", "Retention", "Running Account"].map(s => <option key={s}>{s}</option>)
                      : billTypes.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                    <option value="__add_new__" data-testid="add-bill-type-option">+ Add new Bill Type…</option>
                  </select>
                </CompactField>
              )}
              <CompactField label="Branch *" required>
                <select data-testid="inv-branch" required value={form.branch_id || ""} onChange={e => {
                  const b = branches.find(x => x.id === e.target.value);
                  setForm({ ...form, branch_id: e.target.value, branch_name: b?.branch_name || "",
                            // Reset warehouse when branch changes — the
                            // warehouse useEffect will refetch the
                            // branch-scoped list and auto-select default.
                            warehouse_id: "", warehouse_name: "" });
                }} className={compactInput}>
                  <option value="">— Select Branch —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}{b.gst_number ? ` (${b.gst_number})` : ""}</option>)}
                </select>
              </CompactField>
              {(!isNote) && (
                <CompactField
                  label={`Warehouse${isPureSales ? "" : " *"}`}
                  required={!isSales}
                >
                  <select
                    data-testid="inv-warehouse"
                    required={!isSales}
                    value={form.warehouse_id || ""}
                    onChange={e => {
                      const w = warehouses.find(x => x.id === e.target.value);
                      setForm(f => ({
                        ...f,
                        warehouse_id: e.target.value,
                        warehouse_name: w?.warehouse_name || "",
                      }));
                    }}
                    className={compactInput}
                    disabled={!form.branch_id}
                  >
                    <option value="">
                      {form.branch_id
                        ? (warehouses.length
                            ? "— Select Warehouse —"
                            : "— No warehouse available for this branch —")
                        : "— Pick a branch first —"}
                    </option>
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id} data-testid={`inv-warehouse-opt-${w.warehouse_code}`}>
                        {w.warehouse_code} — {w.warehouse_name}
                        {w.is_default ? " · Default" : ""}
                      </option>
                    ))}
                  </select>
                </CompactField>
              )}
              <CompactField label={`${isSales ? "Customer" : "Vendor"} *`} required>
                <SearchableSelect
                  value={form[`${partnerKey}_id`] || ""}
                  onChange={(id) => onPartnerChange(id)}
                  items={partners}
                  getKey={(p) => p.id}
                  getLabel={(p) => {
                    const code = p[`${partnerKey}_code`] || p.code || "";
                    return code ? `${code} · ${p.name || ""}` : (p.name || "");
                  }}
                  getSubLabel={(p) => [p.gst_number || p.gstin, p.city, p.branch_name].filter(Boolean).join(" · ") || ""}
                  getSearchText={(p) => `${p[`${partnerKey}_code`] || ""} ${p.code || ""} ${p.name || ""} ${p.gst_number || p.gstin || ""} ${p.mobile || ""} ${p.city || ""} ${p.state || ""} ${p.pan || ""}`}
                  placeholder={`Search ${isSales ? "customer" : "vendor"} by code or name…`}
                  searchPlaceholder={`Type code / name / GSTIN / city…`}
                  required
                  testid="inv-partner"
                  inputClassName={compactInput}
                />
              </CompactField>
              <CompactField label="Email"><input type="email" value={form.email} onChange={e => set("email", e.target.value)} className={compactInput} /></CompactField>
              <CompactField label="Salesman">
                <input list={`members-list-${type}`} value={form.salesman} onChange={e => set("salesman", e.target.value)}
                  placeholder="Type to search…" data-testid="inv-salesman" className={compactInput} />
                <datalist id={`members-list-${type}`}>
                  {members.map(m => <option key={m.id} value={m.name}>{m.designation ? `${m.designation} · ` : ""}{m.member_code || ""}</option>)}
                </datalist>
              </CompactField>
              <CompactField label="Service Period"><input data-testid="inv-service-period" value={form.service_period || ""} onChange={e => set("service_period", e.target.value)} placeholder="e.g. Jan–Mar 2026" className={compactInput} /></CompactField>
              {(() => {
                // Phase C-Lite — Project Reporting Period picker.
                //   SI: always · PI: only when a Service/blank-type line exists
                //   CN/DN: only when NOT linked to a mother SI record (linked
                //   notes inherit the SI's period) · PO: never.
                const showRp = isPureSales
                  || (!isSales && !isPO && (form.line_items || []).some(l => !["Inventory", "Asset"].includes((l.item_type || "").trim())))
                  || (isNote && !(form.reference_invoice_id || "").trim());
                if (!showRp) return null;
                const rpApproved = !!invoice && form.status === "Approved";
                return (
                  <CompactField label="Project Reporting Period">
                    {rpApproved ? (
                      <ApprovedReportingPeriod
                        resource={resource}
                        docId={invoice.id}
                        value={form.reporting_period || ""}
                        onSaved={(p) => set("reporting_period", p)}
                        voucherLabel={`${docNoun} ${form.invoice_number || ""}`}
                        testid="inv-reporting-period"
                        className={compactInput}
                      />
                    ) : (
                      <ReportingPeriodInput
                        value={form.reporting_period || ""}
                        onChange={(v) => { rpTouchedRef.current = true; set("reporting_period", v); }}
                        testid="inv-reporting-period"
                        className={compactInput}
                      />
                    )}
                  </CompactField>
                );
              })()}
              {isSales ? (
                (() => {
                  const sel = partners.find(p => p.id === form[`${partnerKey}_id`]);
                  const pocs = sel?.pocs || [];
                  return (
                    <>
                      <CompactField label="Client POC">
                        <input list={`client-pocs-${type}`} value={form.agent || ""} onChange={e => set("agent", e.target.value)}
                          placeholder={pocs.length ? "Search POC…" : "Pick a customer first"}
                          disabled={!sel} data-testid="inv-client-poc" className={compactInput} />
                        <datalist id={`client-pocs-${type}`}>
                          {pocs.map((p, i) => <option key={i} value={p.name}>{p.designation ? `${p.designation} · ` : ""}{p.phone || p.email || ""}</option>)}
                        </datalist>
                      </CompactField>
                      <CompactField label="Alternate Order No">
                        <input data-testid="inv-alternate-order-no" value={form.alternate_order_no || ""}
                          onChange={e => set("alternate_order_no", e.target.value)}
                          placeholder="Customer / client order ref" className={compactInput} />
                      </CompactField>
                      <CompactField label="Alternate Order No Date">
                        <input type="date" data-testid="inv-alternate-order-no-date" value={form.alternate_order_no_date || ""}
                          onChange={e => set("alternate_order_no_date", e.target.value)}
                          className={compactInput} />
                      </CompactField>
                    </>
                  );
                })()
              ) : (
                <CompactField label="Agent">
                  <input list={`agent-members-${type}`} value={form.agent || ""} onChange={e => set("agent", e.target.value)}
                    placeholder="Search member…" data-testid="inv-agent" className={compactInput} />
                  <datalist id={`agent-members-${type}`}>
                    {members.map(m => <option key={m.id} value={m.name}>{m.designation ? `${m.designation} · ` : ""}{m.member_code || ""}</option>)}
                  </datalist>
                </CompactField>
              )}
            </div>

            {/* COLUMN 2 — Document numbering & dates */}
            <div className="space-y-1">
              <CompactField label={isPO ? "PO Date *" : "Date *"} required><input type="date" required value={form.invoice_date} onChange={e => set("invoice_date", e.target.value)} className={compactInput} /></CompactField>
              <CompactField label={isPO ? "PO No *" : "Invoice No *"} required>
                <VoucherNumberInput
                  module={
                    isCN ? "sales_credit_note"
                    : isDN ? "sales_debit_note"
                    : isPureSales ? "sales_invoice"
                    : (isPO ? "purchase_order" : "purchase_invoice")
                  }
                  value={form.invoice_number}
                  onChange={v => set("invoice_number", v)}
                  testidPrefix="inv-number"
                />
              </CompactField>
              {isPO && !invoice && directOps.enabled && (
                <CompactField label="PO Flow">
                  <div className="flex gap-1">
                    <button type="button" data-testid="po-flow-standard"
                      onClick={() => setPoFlow("standard")}
                      className={`flex-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${poFlow === "standard" ? "bg-[#1A1D21] border-[#1A1D21] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-slate-500"}`}>
                      Standard
                    </button>
                    <button type="button" data-testid="po-flow-ops"
                      onClick={() => { setPoFlow("ops"); setForm(f => ({ ...f, project_id: "", project_name: "" })); setProjectSearch(null); }}
                      className={`flex-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${poFlow === "ops" ? "bg-sky-700 border-sky-700 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-sky-600"}`}>
                      Ops Project (No PR)
                    </button>
                  </div>
                  {poFlow === "ops" && (
                    <div className="text-[9px] text-sky-700 mt-0.5 font-bold" data-testid="po-flow-ops-hint">
                      Creates a DRAFT Project Operations PO directly — no PR needed. Submit it for approval after saving to unlock GRN / PI.
                    </div>
                  )}
                </CompactField>
              )}
              <CompactField label="Project *" required>
                {opsDirectCreate ? (
                  /* Jun-2026 (user request) — type-to-search + dropdown for the
                     Ops project picker (was a plain select). */
                  <SearchableSelect
                    testid="po-ops-project-select"
                    required
                    value={form.project_id}
                    items={directOps.projects}
                    onChange={(id, p) => setForm(f => ({ ...f, project_id: id || "", project_name: p?.project_name || "" }))}
                    getKey={(p) => p.id}
                    getLabel={(p) => `${p.project_code} · ${p.project_name || ""}`}
                    getSearchText={(p) => `${p.project_code || ""} ${p.project_name || ""}`.toLowerCase()}
                    placeholder={`— Pick Operational Project (${directOps.projects.length}) —`}
                    searchPlaceholder="Type project code or name…"
                    inputClassName={compactInput}
                  />
                ) : (() => {
                  const projectLabel = (p) => `${p.project_code} · ${p.project_name || p.name || ""}`;
                  const sel = projects.find(p => p.id === form.project_id);
                  const boundLabel = sel ? projectLabel(sel) : (form.project_id ? (form.project_name || "") : "");
                  const displayValue = projectSearch !== null ? projectSearch : boundLabel;
                  return (
                    <>
                      <input data-testid="inv-project" type="text" list="inv-project-list" required
                        value={displayValue}
                        disabled={isPO && invoice?.procurement_flow === "PROJECT_OPERATIONS"}
                        title={isPO && invoice?.procurement_flow === "PROJECT_OPERATIONS" ? "Project is locked on a Project Operations Draft PO" : undefined}
                        placeholder={`Search project by code or name (${projects.length})…`}
                        onChange={e => {
                          const raw = e.target.value || "";
                          setProjectSearch(raw);
                          const v = raw.trim().toLowerCase();
                          const match = projects.find(p => projectLabel(p).toLowerCase() === v);
                          if (match) { set("project_id", match.id); setProjectSearch(null); }
                          else if (!v) set("project_id", "");
                        }}
                        onBlur={() => setProjectSearch(null)}
                        className={compactInput} />
                      <datalist id="inv-project-list">
                        {projects.map(p => <option key={p.id} value={`${p.project_code} · ${p.project_name || p.name || ""}`} />)}
                      </datalist>
                    </>
                  );
                })()}
              </CompactField>
              {isPO && <CompactField label="Expected Delivery"><input type="date" data-testid="po-expected-delivery" value={form.expected_delivery_date || ""} onChange={e => set("expected_delivery_date", e.target.value)} className={compactInput} /></CompactField>}
              <CompactField label={isPO ? "Vendor Quote Ref" : (!isSales ? "Ref No *" : "Ref No")}><input data-testid="inv-ref-no" required={!isSales && !isPO} value={form.ref_no} onChange={e => set("ref_no", e.target.value)} className={compactInput} /></CompactField>
              <CompactField label="Ref Date"><input type="date" value={form.ref_date} onChange={e => set("ref_date", e.target.value)} className={compactInput} /></CompactField>
              {isSales && (() => {
                // RC Number is driven from the selected Client's Active
                // projects. Sprint 3.11 — searchable combobox: operator
                // types any part of the project code, project name OR
                // contract number, and the matching projects appear in
                // the live dropdown. Picking one stores that project's
                // current `project_contract_number` on the SI. Falls
                // back to free-text so legacy off-master numbers survive
                // an edit-save cycle.
                const clientName = partners.find(p => p.id === form[`${partnerKey}_id`])?.name || "";
                const clientActiveProjects = clientName
                  ? projects.filter(p => (p.client_name || "").trim().toLowerCase() === clientName.trim().toLowerCase()
                      && (p.status === "Active" || !p.status))
                  : [];
                const hasMatch = !!form.rc_number && clientActiveProjects.some(p => (p.project_contract_number || "") === form.rc_number);
                return (
                  <CompactField label="RC Number">
                    <RCNumberCombobox
                      value={form.rc_number || ""}
                      onChange={(v) => set("rc_number", v)}
                      projects={clientActiveProjects}
                      disabled={!form[`${partnerKey}_id`]}
                      compactInput={compactInput}
                      partnerSelected={!!form[`${partnerKey}_id`]}
                    />
                    {form.rc_number && (
                      <div className="text-[10px] text-slate-500 mt-0.5" data-testid="inv-rc-number-hint">
                        Contract No: <span className="font-bold text-slate-700">{form.rc_number}</span>
                        {!hasMatch && (
                          <span className="ml-1 text-amber-700">(legacy / off-master)</span>
                        )}
                      </div>
                    )}
                  </CompactField>
                );
              })()}
              {!isSales && !isPO && <CompactField label="PO No"><input value={form.po_number || ""} onChange={e => set("po_number", e.target.value)} className={compactInput} /></CompactField>}
              {!isSales && !isPO && <CompactField label="GRN No"><input value={form.grn_number || ""} onChange={e => set("grn_number", e.target.value)} className={compactInput} /></CompactField>}
              {!isSales && !isPO && !isNote && (
                <CompactField label="Payment Req No (optional)">
                  <SearchableSelect
                    testid="pi-payment-request-no"
                    value={form.payment_request_number || ""}
                    onChange={(v) => set("payment_request_number", v || "")}
                    items={(() => {
                      // Requests tied to a PO on this PI ALWAYS show —
                      // even if the request's snapshotted vendor_id differs
                      // from the PI vendor (legacy/contractor-bound POs).
                      const poNums = new Set([form.po_number, form._pulled_from_po,
                        ...(form.line_items || []).map((l) => l.source_po_number)].filter(Boolean));
                      const rows = openPayReqs
                        .filter((r) => poNums.has(r.po_number) || !form.vendor_id || r.vendor_id === form.vendor_id)
                        .map((r) => ({ ...r, id: r.request_number }));
                      const cur = (form.payment_request_number || "").trim();
                      if (cur && !rows.some((r) => r.id === cur)) {
                        rows.unshift({ id: cur, request_number: cur, po_number: "", project_code: "(linked)" });
                      }
                      return rows;
                    })()}
                    getLabel={(r) => r.request_number}
                    getSubLabel={(r) => [r.po_number, r.project_code, r.vendor_name].filter(Boolean).join(" · ")}
                    getSearchText={(r) => `${r.request_number} ${r.po_number} ${r.vendor_name} ${r.project_code}`}
                    placeholder={form.vendor_id ? "Pick an open request…" : "Pick vendor first (or search all)…"}
                    searchPlaceholder="Search PPR / PO number…"
                  />
                </CompactField>
              )}
              {isSales && <CompactField label="Work Order"><input value={form.work_order_no || ""} onChange={e => set("work_order_no", e.target.value)} className={compactInput} /></CompactField>}
              <CompactField label="Tax">
                <select value={form.tax_treatment} onChange={e => set("tax_treatment", e.target.value)} className={compactInput}>
                  {["Exclusive", "Inclusive"].map(s => <option key={s}>{s}</option>)}
                </select>
              </CompactField>
              <CompactField label="GST Number">
                <input data-testid="inv-gst-number" value={form.gst_number} onChange={e => set("gst_number", e.target.value)} className={compactInput} />
                {isTaxable && buyerState && (
                  <div className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${isIntraState ? "text-emerald-700" : "text-orange-700"}`}>
                    {sellerState ? `Seller ${sellerState} · Buyer ${buyerState} → ${isIntraState ? "Intra-state (CGST + SGST)" : "Inter-state (IGST)"}` : "Set branch / company GST to detect place of supply"}
                  </div>
                )}
              </CompactField>
              <CompactField label="Currency">
                <select value={form.currency} onChange={e => set("currency", e.target.value)} className={compactInput}>
                  {["INR", "USD", "EUR", "GBP", "AED"].map(c => <option key={c}>{c}</option>)}
                </select>
              </CompactField>
              {isSales && (
                <CompactField label="Sub Vendor Name">
                  <input list={`sub-vendor-list-${type}`}
                         value={form.sub_vendor_name || ""}
                         onChange={e => set("sub_vendor_name", e.target.value)}
                         placeholder="Type to search vendor library…"
                         data-testid="inv-sub-vendor-name"
                         className={compactInput} />
                  <datalist id={`sub-vendor-list-${type}`}>
                    {/* Pre-defined sentinel — kept on top so operators can
                        pick it in a single keystroke when work is done
                        in-house and there is no third-party sub-vendor. */}
                    <option value="Radiants (Own Company)" />
                    {subVendors.map(v => (
                      <option key={v.id} value={v.name}>
                        {v.vendor_code ? `${v.vendor_code} · ` : ""}{v.skill_type || ""}
                      </option>
                    ))}
                  </datalist>
                </CompactField>
              )}
            </div>

            {/* COLUMN 3 — Bill To / Ship To / Payment */}
            <div className="space-y-3">
              <div className="border border-slate-200">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center gap-2">📄 Bill To</div>
                <textarea rows={3} value={form.billing_address} onChange={e => set("billing_address", e.target.value)} className="w-full px-3 py-2 text-xs border-0 focus:outline-none resize-none" placeholder="Billing Address" />
              </div>
              <div className="border border-slate-200">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center justify-between gap-2">
                  <span>📦 Ship To {form.shipping_address_label ? <span className="text-[#F97316] normal-case ml-1">· {form.shipping_address_label}</span> : null}</span>
                  {(form.billing_address || "").trim() && (
                    <button type="button" data-testid="ship-same-as-billing"
                      onClick={() => setForm(f => ({ ...f, shipping_address: f.billing_address, shipping_address_label: "Same as Billing" }))}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#F97316] hover:underline">
                      Same as Billing
                    </button>
                  )}
                </div>
                {isSales && activePartner && (
                  <div className="px-3 py-2 bg-blue-50/40 border-b border-slate-200">
                    {shippingOptions.length > 0 ? (
                      <>
                        <input list="ship-search-list" data-testid="ship-search"
                          placeholder={`🔍 Search ${shippingOptions.length} saved shipping address${shippingOptions.length === 1 ? "" : "es"} (label / city / address)…`}
                          onChange={e => {
                            const v = e.target.value;
                            const idx = shippingOptions.findIndex(s => (s.label || "").trim().toLowerCase() === v.trim().toLowerCase());
                            if (idx >= 0) { pickShippingAddress(idx); e.target.value = ""; }
                          }}
                          className="w-full text-xs border border-slate-300 px-3 py-1.5 bg-white" />
                        <datalist id="ship-search-list">
                          {shippingOptions.map((s, i) => <option key={i} value={s.label || `Addr ${i + 1}`} />)}
                        </datalist>
                      </>
                    ) : (
                      <p className="text-[10px] text-slate-500 italic">No saved shipping addresses for this client — edit shipping address directly below, or add labelled addresses in the Client master.</p>
                    )}
                  </div>
                )}
                <textarea rows={3} value={form.shipping_address} onChange={e => set("shipping_address", e.target.value)} className="w-full px-3 py-2 text-xs border-0 focus:outline-none resize-none" placeholder="Shipping Address — start typing in the search box above to pick saved" />
              </div>
              <CompactField label="Payment Terms"><input value={form.payment_terms} onChange={e => set("payment_terms", e.target.value)} placeholder="e.g. 30 Days" className={compactInput} /></CompactField>
              <CompactField label="Due Date"><input type="date" value={form.due_date || ""} onChange={e => set("due_date", e.target.value)} className={compactInput} /></CompactField>
              {isSales && (
                <CompactField label="JMS Number">
                  <input
                    data-testid="inv-jms-number"
                    value={form.jms_number || ""}
                    onChange={e => set("jms_number", e.target.value)}
                    placeholder="e.g. JMS/2526/00123"
                    className={compactInput}
                  />
                </CompactField>
              )}
            </div>
          </div>

          <div>
            {form.estimate_number && (
              <div className="bg-orange-50 border border-orange-200 px-4 py-2 mb-3 text-xs text-orange-900 rounded-sm">
                <strong>← Pulled from {form.estimate_number}</strong>. Orange-highlighted Qty fields show available limit from estimate; you can pull less now and the rest stays available for future invoices.
              </div>
            )}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Line Items — pick from Item Library or type custom</h3>
              <button type="button" data-testid="add-line-btn" onClick={addLine} className="text-xs font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1"><Plus size={12} /> Add Row</button>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filter items by Group:</label>
              <select data-testid="group-filter" value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                      className="border border-slate-300 px-3 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none">
                <option value="">{`All Groups (${items.length} items)`}</option>
                {groups.map(g => {
                  const count = items.filter(it => _itemInGroup(it, g)).length;
                  return <option key={g.id} value={g.id}>{`${g.name} (${count})`}</option>;
                })}
              </select>
              {groupFilter && (
                <button type="button" onClick={() => setGroupFilter("")} className="text-[10px] text-slate-500 hover:text-[#F97316] underline">clear</button>
              )}
            </div>
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: 1500 }}>
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-2 px-2 w-44">Item</th>
                    <th className="text-left py-2 px-2 w-24">Item No.</th>
                    <th className="text-left py-2 px-2 w-24">Item Type</th>
                    <th className="text-left py-2 px-2">Description</th>
                    <th className="text-right py-2 px-2 w-20">Qty</th>
                    <th className="text-left py-2 px-2 w-16">Unit</th>
                    <th className="text-right py-2 px-2 w-24">Unit Amt</th>
                    <th className="text-right py-2 px-2 w-16">{isSales ? "Mgmt %" : "Disc %"}</th>
                    <th className="text-right py-2 px-2 w-20">{isSales ? "Mgmt Amt" : "Disc Amt"}</th>
                    <th className="text-right py-2 px-2 w-24">Total Unit Amt</th>
                    <th className="text-right py-2 px-2 w-14">GST%</th>
                    <th className="text-right py-2 px-2 w-24">GST Amt</th>
                    <th className="text-right py-2 px-2 w-28">Taxable Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.line_items.map((l, i) => {
                    const M = TYPE_PILL[l.item_type] || {};
                    const baseRate = Number(l.base_rate ?? l.rate ?? 0);
                    const feePct = isSales
                      ? (l.management_fee_type === "percent" ? Number(l.management_fee_value || 0) : 0)
                      : (l.purchase_discount_type === "percent" ? Number(l.purchase_discount_value || 0) : 0);
                    const feeAmt = isSales ? Number(l.management_fee_amount || 0) : Number(l.purchase_discount_amount || 0);
                    const expanded = !!l._expanded;
                    return (
                    <React.Fragment key={i}>
                    <tr className="border-b border-slate-100">
                      <td className="p-1">
                        <SearchableSelect
                          value={l.item_id || ""}
                          onChange={(id) => pickItem(i, id)}
                          items={filteredItems}
                          getKey={(it) => it.id}
                          getLabel={(it) => `${it.item_code ? `${it.item_code} · ` : ""}${it.name}${it.item_type ? ` · ${it.item_type}` : ""}`}
                          getSubLabel={(it) => [it.category, it.unit, it.hsn_sac].filter(Boolean).join(" · ")}
                          getSearchText={(it) => `${it.item_code || ""} ${it.name || ""} ${it.category || ""} ${it.item_type || ""} ${it.hsn_sac || ""}`}
                          placeholder="Search item…"
                          searchPlaceholder="Code / name / category / HSN…"
                          testid={`line-item-picker-${i}`}
                          inputClassName="border-transparent"
                          allowClear
                        />
                      </td>
                      <td className="p-1"><input value={l.item_code || ""} onChange={e => setLine(i, "item_code", e.target.value)} className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs" /></td>
                      <td className="p-1">
                        {l.item_type ? (
                          <span className={`inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${M.color || "bg-slate-100 text-slate-700"}`}>{l.item_type}</span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="p-1"><input required value={l.description} onChange={e => setLine(i, "description", e.target.value)} className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none" /></td>
                      <td className="p-1">
                        <input type="number" step="0.01" max={l._max_quantity ?? undefined} value={l.quantity} onChange={e => setLine(i, "quantity", e.target.value)} className={`w-full text-right px-1.5 py-1.5 border focus:border-[#F97316] focus:outline-none ${l._max_quantity !== undefined ? "border-[#F97316]/40 bg-orange-50 font-bold" : "border-transparent"}`} title={l._max_quantity !== undefined ? `Available from estimate: ${l._max_quantity}` : ""} />
                        {l._max_quantity !== undefined && (
                          <div className="text-[9px] text-orange-700 mt-0.5 text-right pr-1">avail: <span className="font-bold">{l._max_quantity}</span></div>
                        )}
                      </td>
                      <td className="p-1"><input value={l.unit} onChange={e => setLine(i, "unit", e.target.value)} className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs" /></td>
                      <td className="p-1"><input data-testid={`line-base-rate-${i}`} type="number" step="0.01" value={Number(l.base_rate ?? l.rate ?? 0)} onChange={e => setLine(i, "base_rate", e.target.value)} className="w-full text-right px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs" /></td>
                      <td className="p-1 text-right tabular text-[11px]">
                        {isSales ? (
                          <span className="text-slate-600">{feePct ? `${feePct}%` : "—"}</span>
                        ) : (
                          <input type="number" step="0.01" min="0" value={l.purchase_discount_type === "percent" ? (l.purchase_discount_value || "") : ""}
                            onChange={e => setLine(i, "purchase_discount_value", e.target.value === "" ? 0 : Number(e.target.value))}
                            data-testid={`line-disc-pct-${i}`}
                            placeholder="0"
                            className="w-full text-right px-1 py-1 border border-transparent focus:border-[#F97316] focus:outline-none text-xs"/>
                        )}
                      </td>
                      <td className="p-1 text-right tabular text-[11px]">
                        {isSales ? (
                          <span className="text-slate-600">{feeAmt ? formatINR(feeAmt) : "—"}</span>
                        ) : (
                          <input type="number" step="0.01" min="0" value={l.purchase_discount_amount || ""}
                            onChange={e => setLine(i, "purchase_discount_amount", e.target.value === "" ? 0 : Number(e.target.value))}
                            data-testid={`line-disc-amt-${i}`}
                            placeholder="0"
                            className="w-full text-right px-1 py-1 border border-transparent focus:border-[#F97316] focus:outline-none text-xs"/>
                        )}
                      </td>
                      <td className="p-1 text-right tabular text-xs font-bold text-[#F97316]">{formatINR(Number(l.rate || 0))}</td>
                      <td className="p-1"><input type="number" step="0.01" value={isTaxable ? l.gst_rate : 0} onChange={e => setLine(i, "gst_rate", e.target.value)} disabled={!isTaxable} title={isTaxable ? "" : "Disabled — Bill of Supply (Non GST)"} className={`w-full text-right px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs ${!isTaxable ? "bg-slate-100 text-slate-400 cursor-not-allowed" : ""}`} /></td>
                      <td className="p-1 text-right tabular pr-2 text-xs text-slate-700">{formatINR(Number(l.amount || 0) * (isTaxable ? Number(l.gst_rate || 0) : 0) / 100)}</td>
                      <td className="p-1 text-right tabular pr-2 font-bold text-xs">{formatINR(l.amount)}</td>
                      <td className="p-1 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {l.item_id && (
                            <button
                              type="button"
                              data-testid={`inv-toggle-brand-${i}`}
                              onClick={() => setLine(i, "_expanded", !expanded)}
                              title="Edit Brand / Warranty / Order Number / Order Month / Client Server for this line only"
                              className={`p-1 ${expanded ? "text-[#F97316]" : "text-slate-400 hover:text-[#F97316]"}`}>
                              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            </button>
                          )}
                          <button type="button" onClick={() => removeLine(i)} className="text-red-500"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-orange-50/40 border-b border-slate-100">
                        <td colSpan={14} className="px-3 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                              Line-level overrides — these update only this voucher, not the Item master
                            </div>
                            {form.line_items.length > 1 && (
                              <button
                                type="button"
                                data-testid={`inv-apply-all-${i}`}
                                onClick={() => {
                                  if (!window.confirm(`Copy Brand / Warranty / Order Number / Order Month / Client Server from this line to all ${form.line_items.length - 1} other line(s)?`)) return;
                                  const lines = [...form.line_items];
                                  const payload = {
                                    brand: lines[i].brand || "",
                                    warranty: lines[i].warranty || "",
                                    order_number: lines[i].order_number || "",
                                    order_month: lines[i].order_month || "",
                                    client_server: lines[i].client_server || "",
                                  };
                                  for (let j = 0; j < lines.length; j++) {
                                    if (j !== i) lines[j] = { ...lines[j], ...payload };
                                  }
                                  setForm({ ...form, line_items: lines });
                                  toast.success(`Copied to ${lines.length - 1} other line(s)`);
                                }}
                                className="text-[10px] font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1 inline-flex items-center gap-1"
                                title="Apply these 5 values to every other line in this voucher">
                                <Check size={11} /> Apply to all lines
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Brand / Make</div>
                              <input
                                data-testid={`inv-line-brand-${i}`}
                                value={l.brand || ""}
                                onChange={e => setLine(i, "brand", e.target.value)}
                                placeholder="e.g. Schneider / Havells / Dell"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Warranty</div>
                              <input
                                data-testid={`inv-line-warranty-${i}`}
                                value={l.warranty || ""}
                                onChange={e => setLine(i, "warranty", e.target.value)}
                                placeholder="e.g. 2 Years On-site"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Order Number</div>
                              <input
                                data-testid={`inv-line-order-number-${i}`}
                                value={l.order_number || ""}
                                onChange={e => setLine(i, "order_number", e.target.value)}
                                placeholder="Client PO no. (free text)"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Order Month</div>
                              <input
                                data-testid={`inv-line-order-month-${i}`}
                                value={l.order_month || ""}
                                onChange={e => setLine(i, "order_month", e.target.value)}
                                placeholder="e.g. Mar 2026"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Client Server</div>
                              <input
                                data-testid={`inv-line-client-server-${i}`}
                                value={l.client_server || ""}
                                onChange={e => setLine(i, "client_server", e.target.value)}
                                placeholder="e.g. CSpur-DC1"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>);
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {type === "purchase" && (
            <ProjectPIPanel lines={form.line_items}
              onSetReason={(i, reason) => setForm(f => ({
                ...f,
                line_items: f.line_items.map((l, j) => j === i ? { ...l, pi_ahead_reason: reason } : l),
              }))} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              {isSales && (
                <Field label="Payment Status">
                  <select value={form.payment_status} onChange={e => set("payment_status", e.target.value)} className={inputCls}>
                    {["Pending", "Partial", "Paid"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              )}
              {!isSales && !isPO && (
                <Field label={`TDS Amount (INR)${form.tds_rate_applied > 0 && !form.tds_user_edited ? ` — auto @ ${form.tds_rate_applied}%` : (form.tds_user_edited ? " — manual override" : "")}`}>
                  <input type="number" step="0.01" value={form.tds_amount}
                    onChange={e => setForm(f => ({ ...f, tds_amount: e.target.value, tds_user_edited: true }))}
                    className={inputCls} />
                </Field>
              )}
              {!isSales && (
                <label className="flex items-center gap-2 cursor-pointer border border-slate-200 bg-slate-50/40 px-3 py-2">
                  <input type="checkbox" data-testid="inv-enable-roundup" checked={!!form.enable_round_up}
                    onChange={e => set("enable_round_up", e.target.checked)} />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Round Up Total to nearest ₹1
                  </span>
                </label>
              )}
              {isSales && (
                <div className="space-y-2 border border-slate-200 bg-slate-50/40 p-3" data-testid="si-tds-deductions">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid="si-enable-tds" checked={!!form.enable_tds}
                      onChange={e => set("enable_tds", e.target.checked)} />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Enable TDS @ {Number(form.tds_rate || 2)}% on Taxable Amount
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid="si-enable-roundup" checked={!!form.enable_round_up}
                      onChange={e => set("enable_round_up", e.target.checked)} />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Round Up Total to nearest ₹1
                    </span>
                  </label>
                  <div className="pt-2 border-t border-slate-200 space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Special Deductions (Debtor Ledger only)</div>
                    {(form.special_deductions || []).map((d, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`si-deduction-row-${i}`}>
                        <input type="checkbox" data-testid={`si-deduction-toggle-${i}`} checked={!!d.enabled}
                          onChange={e => {
                            const next = [...(form.special_deductions || [])];
                            next[i] = { ...next[i], enabled: e.target.checked, amount: e.target.checked ? next[i].amount : 0 };
                            set("special_deductions", next);
                          }} />
                        <input list={`deduction-types-list`} data-testid={`si-deduction-name-${i}`}
                          placeholder="Deduction name (e.g. Retention 5%)" value={d.name || ""}
                          onChange={e => {
                            const next = [...(form.special_deductions || [])];
                            next[i] = { ...next[i], name: e.target.value };
                            // Auto-enable when user starts typing a name
                            if (e.target.value.trim() && !next[i].enabled) next[i].enabled = true;
                            set("special_deductions", next);
                          }} className={`${inputCls} flex-1 min-w-[180px]`} />
                        <input type="number" step="0.01" data-testid={`si-deduction-amount-${i}`}
                          placeholder="Amount" value={d.amount || ""}
                          disabled={!d.enabled}
                          onChange={e => {
                            const next = [...(form.special_deductions || [])];
                            next[i] = { ...next[i], amount: Number(e.target.value) || 0 };
                            set("special_deductions", next);
                          }} className={`${inputCls} w-32 ${!d.enabled ? "opacity-40" : ""}`} />
                      </div>
                    ))}
                    <datalist id="deduction-types-list">
                      {deductionTypes.map(t => <option key={t.id} value={t.name} />)}
                    </datalist>
                  </div>
                </div>
              )}
              <Field label="Remarks"><textarea rows={2} value={form.remarks || ""} onChange={e => set("remarks", e.target.value)} className={inputCls} /></Field>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-5 space-y-2 self-start">
              <Row label="Subtotal" value={formatINR(subtotal)} />
              {isTaxable && isIntraState && (
                <>
                  <Row label="CGST" value={formatINR(cgst)} />
                  <Row label="SGST" value={formatINR(sgst)} />
                  <Row label="Total GST" value={formatINR(cgst + sgst)} bold />
                </>
              )}
              {isTaxable && !isIntraState && (
                <Row label="IGST" value={formatINR(igst)} />
              )}
              {!isTaxable && (
                <Row label="GST" value="— Non GST —" />
              )}
              {form.enable_round_up && (
                <Row label="Round Up Adjustment"
                  value={`${roundAdj >= 0 ? "+" : ""}${formatINR(roundAdj)}`} />
              )}
              <div className="border-t border-slate-300 pt-3 mt-2">
                <Row label="Total" value={formatINR(total)} big bold />
              </div>
              {!isSales && !isPO && (
                <>
                  <Row label="TDS" value={`- ${formatINR(form.tds_amount || 0)}`} />
                  <Row label="Net Payable" value={formatINR(net)} bold />
                </>
              )}
            </div>
          </div>

          {isSales && (
            <details className="border border-slate-200 bg-slate-50/40 px-4 py-3" data-testid="si-transport-section">
              <summary className="text-xs font-bold uppercase tracking-wider text-slate-700 cursor-pointer">
                Transportation / E-Way Bill Details
                {form.generate_eway && <span className="ml-2 text-[10px] text-[#F97316]">· E-WAY MARKED</span>}
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
                <Field label="Transport Mode">
                  <select data-testid="si-transport-mode" value={form.transport_mode || ""} onChange={e => set("transport_mode", e.target.value)} className={inputCls}>
                    {["", "Road", "Rail", "Air", "Ship"].map(s => <option key={s} value={s}>{s || "—"}</option>)}
                  </select>
                </Field>
                <Field label="Vehicle No"><input data-testid="si-vehicle-no" value={form.vehicle_no || ""} onChange={e => set("vehicle_no", e.target.value)} className={inputCls} /></Field>
                <Field label="Vehicle Type">
                  <select value={form.vehicle_type || ""} onChange={e => set("vehicle_type", e.target.value)} className={inputCls}>
                    {["", "Regular", "Over-Dimensional Cargo (ODC)"].map(s => <option key={s} value={s}>{s || "—"}</option>)}
                  </select>
                </Field>
                <Field label="Transporter Name"><input value={form.transporter_name || ""} onChange={e => set("transporter_name", e.target.value)} className={inputCls} /></Field>
                <Field label="Transporter ID / GSTIN"><input value={form.transporter_id || ""} onChange={e => set("transporter_id", e.target.value)} className={inputCls} /></Field>
                <Field label="LR / Docket No"><input value={form.lr_no || ""} onChange={e => set("lr_no", e.target.value)} className={inputCls} /></Field>
                <Field label="LR Date"><input type="date" value={form.lr_date || ""} onChange={e => set("lr_date", e.target.value)} className={inputCls} /></Field>
                <Field label="Distance (km)"><input type="number" step="0.01" data-testid="si-distance" value={form.distance_km || 0} onChange={e => set("distance_km", Number(e.target.value) || 0)} className={inputCls} /></Field>
                <Field label="Sub-supply Type">
                  <select value={form.sub_supply_type || ""} onChange={e => set("sub_supply_type", e.target.value)} className={inputCls}>
                    {["", "Supply", "Export", "Job Work", "Returned Goods", "SKD/CKD", "Recipient Not Known", "For Own Use", "Exhibition or Fairs"].map(s => <option key={s} value={s}>{s || "—"}</option>)}
                  </select>
                </Field>
                <Field label="Dispatch From (Place)"><input value={form.dispatch_from || ""} onChange={e => set("dispatch_from", e.target.value)} className={inputCls} /></Field>
                <Field label="Delivery To (Place)"><input value={form.delivery_to || ""} onChange={e => set("delivery_to", e.target.value)} className={inputCls} /></Field>
                <Field label="E-Way Bill No (IRN later)"><input value={form.eway_bill_no || ""} onChange={e => set("eway_bill_no", e.target.value)} className={inputCls} placeholder="Auto-filled by future API" /></Field>
                <Field label="">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" data-testid="si-generate-eway" checked={!!form.generate_eway}
                      onChange={e => set("generate_eway", e.target.checked)} />
                    <span className="font-bold uppercase tracking-wider text-slate-700">Generate E-Way Bill on Save (API)</span>
                  </label>
                </Field>
              </div>
            </details>
          )}

          {!isSales && (
            <div className="border border-slate-200 bg-slate-50/50 px-4 py-3" data-testid="pi-attachments-section">
              <AttachmentsField value={form.attachments || []}
                onChange={atts => setForm({ ...form, attachments: atts })}
                label="Supporting Documents"/>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              type="button"
              data-testid="discard-invoice-btn"
              onClick={handleDiscard}
              className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-bold uppercase tracking-wider hover:bg-slate-50 hover:border-rose-400 hover:text-rose-700"
            >
              {irnLocked ? "Close" : "Discard"}
            </button>
            {/* Sprint 3.10.2 — Sync Stock button. Surfaces ONLY for
                Approved Purchase Invoices whose inventory_batch_ids
                count is less than the count of Inventory line items
                — i.e. the half-baked state described in the user's
                bug report. Clicking it POSTs to /sync-stock which
                gap-fills the missing batches + stock entries. */}
            {!isSales && !isPO && invoice && form.status === "Approved" && (() => {
              const invLineCount = (form.line_items || []).filter(
                l => (l.item_type || "Inventory") === "Inventory"
                     && Number(l.quantity || 0) > 0
              ).length;
              const batchCount = (form.inventory_batch_ids || []).length;
              const isHalfBaked = invLineCount > batchCount;
              if (!isHalfBaked) return null;
              return (
                <button
                  type="button"
                  data-testid="sync-stock-btn"
                  disabled={syncingStock}
                  onClick={async () => {
                    if (syncingStock) return;
                    if (!window.confirm(
                      `This PI has ${invLineCount} inventory line(s) but only ` +
                      `${batchCount} batch(es) in stock. Sync Stock will create the ` +
                      `missing ${invLineCount - batchCount} batch(es) + stock entries. ` +
                      `Proceed?`)) return;
                    setSyncingStock(true);
                    try {
                      const r = await api.post(`/purchase-invoices/${invoice.id}/sync-stock`);
                      // Patch form locally so the warning button vanishes
                      // immediately without a full reload (which would
                      // bounce the operator back to the list tab).
                      const updatedPI = r.data?.invoice || {};
                      setForm(prev => ({
                        ...prev,
                        inventory_batch_ids: updatedPI.inventory_batch_ids || prev.inventory_batch_ids,
                        stock_entry_ids:     updatedPI.stock_entry_ids     || prev.stock_entry_ids,
                        posted_to_stock:     updatedPI.posted_to_stock,
                      }));
                      toast.success(
                        `Stock synced — ${r.data?.newly_created_batches || 0} batch(es) ` +
                        `+ ${r.data?.newly_created_stock_entries || 0} stock entry(ies) created.`
                      );
                    } catch (e) {
                      toast.error(
                        "Sync Stock failed: " +
                        (e?.response?.data?.detail || e?.message || "unknown error")
                      );
                    } finally {
                      setSyncingStock(false);
                    }
                  }}
                  title={`Repair half-baked stock: ${batchCount}/${invLineCount} batches present`}
                  className="px-4 py-2.5 bg-amber-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {syncingStock
                    ? "Syncing Stock…"
                    : (<>⚠ Sync Stock ({batchCount}/{invLineCount})</>)}
                </button>
              );
            })()}
            {!projOpsLocked && (
            <button
              data-testid="save-invoice-btn"
              disabled={saving || justSaved}
              type="submit"
              title={irnLocked ? `IRN ${lockedIrn}… is live. Only free-text fields (JMS Number, transport, attachments, ref no, alternate order, remarks) and the TDS tick/rate are editable. Financial & party blocks remain locked.` : ""}
              className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {irnLocked ? <><Lock size={14} /> Save Free-Text Edits</> : saving ? "Saving…" : justSaved ? "Saved ✓" : "Save Invoice"}
            </button>
            )}
          </div>
        </form>
      </div>
      {poPickerOpen && (
        <PoPickerModal
          vendorId={form[`${partnerKey}_id`]}
          projectId={form.project_id}
          onCancel={() => setPoPickerOpen(false)}
          onConfirm={(picked) => {
            // picked = {po_id, po_number, vendor_id, vendor_name, project_id, project_name, branch_id, branch_name, lines:[{src_idx, qty, ...lineItem}]}
            const newLines = picked.lines.map(l => ({
              item_id: l.item_id || "",
              item_code: l.item_code || "",
              description: l.description || "",
              quantity: Number(l.qty || 0),
              unit: l.unit || "Nos",
              base_rate: Number(l.rate || 0),
              rate: Number(l.rate || 0),
              gst_rate: Number(l.gst_rate || 0),
              amount: Number((l.qty || 0) * (l.rate || 0)),
              item_type: l.item_type || "",
              pack_qty: 1, pack_unit: "Box",
              source_po_id: picked.po_id,
              source_po_number: picked.po_number,
              source_po_line_index: l.src_idx,
              pi_line_uid: "",
              pi_ahead_reason: "",
              _remaining_quantity: l._remaining_quantity,
            }));
            // Auto-fill vendor/project/branch if blank; auto-link the open
            // payment request of the pulled PO when exactly one exists.
            const openForPo = openPayReqs.filter((r) => r.po_number === picked.po_number);
            setForm(f => ({
              ...f,
              [`${partnerKey}_id`]: f[`${partnerKey}_id`] || picked.vendor_id,
              vendor_name: f.vendor_name || picked.vendor_name,
              project_id: f.project_id || picked.project_id,
              project_name: f.project_name || picked.project_name,
              branch_id: f.branch_id || picked.branch_id,
              branch_name: f.branch_name || picked.branch_name,
              _pulled_from_po: picked.po_number,
              payment_request_number: f.payment_request_number ||
                (openForPo.length === 1 ? openForPo[0].request_number : ""),
              line_items: (f.line_items && f.line_items.length > 0 && f.line_items[0].description)
                ? [...f.line_items, ...newLines]   // append (multi-PO pull)
                : newLines,
            }));
            setPoPickerOpen(false);
            toast.success(`Pulled ${newLines.length} line(s) from ${picked.po_number}`);
            if (openForPo.length === 1)
              toast.info(`Linked open payment request ${openForPo[0].request_number}`);
            else if (openForPo.length > 1)
              toast.info(`${openForPo.length} open payment requests on ${picked.po_number} — pick one in "Payment Req No"`);
          }}
        />
      )}
    </div>
  );
}

function PoPickerModal({ vendorId, projectId, onCancel, onConfirm }) {
  const [pos, setPos] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState("");
  const [pullQty, setPullQty] = useState({}); // src_idx -> qty
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const params = {};
    if (vendorId) params.vendor_id = vendorId;
    if (projectId) params.project_id = projectId;
    api.get("/purchase-orders/_pickable", { params })
      .then(r => setPos(r.data || []))
      .catch(() => setPos([]))
      .finally(() => setLoading(false));
  }, [vendorId, projectId]);
  const visible = !search ? pos : pos.filter(p =>
    (p.po_number || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.vendor_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.project_name || "").toLowerCase().includes(search.toLowerCase())
  );
  const selectedPo = pos.find(p => p.id === selectedPoId);
  function confirm() {
    if (!selectedPo) return;
    const lines = (selectedPo.line_items || [])
      .filter(l => Number(pullQty[l._po_line_index] || 0) > 0)
      .map(l => ({
        src_idx: l._po_line_index,
        qty: Number(pullQty[l._po_line_index] || 0),
        item_id: l.item_id, item_code: l.item_code,
        description: l.description, unit: l.unit,
        rate: l.rate, gst_rate: l.gst_rate,
        item_type: l.item_type, _remaining_quantity: l._remaining_quantity,
      }));
    if (lines.length === 0) { toast.error("Set pull quantity for at least one line"); return; }
    onConfirm({
      po_id: selectedPo.id, po_number: selectedPo.po_number,
      vendor_id: selectedPo.vendor_id, vendor_name: selectedPo.vendor_name,
      project_id: selectedPo.project_id, project_name: selectedPo.project_name,
      branch_id: selectedPo.branch_id, branch_name: selectedPo.branch_name,
      lines,
    });
  }
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" data-testid="po-picker-modal">
      <div className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold">Pull from Purchase Order</h3>
            <div className="text-[11px] text-slate-500 mt-0.5">Select an approved PO and set per-line pull quantity (remaining qty shown).</div>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="px-6 py-3 border-b border-slate-200">
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="po-picker-search"
            placeholder="Search PO no / vendor / project…"
            className="w-full px-3 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none"/>
        </div>
        <div className="flex-1 overflow-hidden flex">
          <div className="w-1/3 border-r border-slate-200 overflow-y-auto">
            {loading ? <div className="p-6 text-center text-slate-400 text-xs">Loading…</div> :
              visible.length === 0 ? <div className="p-6 text-center text-slate-400 text-xs">
                No approved POs with remaining qty{vendorId ? " for this vendor" : ""}.
              </div> :
                visible.map(p => (
                  <button key={p.id} type="button" onClick={() => { setSelectedPoId(p.id); setPullQty({}); }}
                    data-testid={`po-picker-row-${p.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-orange-50/30 ${selectedPoId === p.id ? "bg-orange-50 border-l-2 border-l-[#F97316]" : ""}`}>
                    <div className="text-xs font-bold text-slate-900">{p.po_number}
                      {p.procurement_flow === "PROJECT_OPERATIONS" && (
                        <span className="ml-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded bg-[#F97316] text-white uppercase tracking-wider" data-testid={`po-picker-project-badge-${p.id}`}>Project Procurement</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-600">{p.vendor_name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{p.project_name}</div>
                    <div className="text-[10px] text-[#F97316] font-bold mt-0.5">{p.status}</div>
                  </button>
                ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {!selectedPo ? <div className="p-10 text-center text-slate-400 text-sm">Select a PO from the left to set pull quantities.</div> :
              <div className="p-4">
                <table className="w-full text-xs">
                  <thead className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left py-2 px-2">Item</th>
                      <th className="text-right py-2 px-2 w-16">Ordered</th>
                      <th className="text-right py-2 px-2 w-16">Already Pulled</th>
                      <th className="text-right py-2 px-2 w-20">Remaining</th>
                      <th className="text-right py-2 px-2 w-24">Pull Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedPo.line_items || []).map(l => {
                      const remaining = Number(l._remaining_quantity || 0);
                      const pulled = Number(l.invoiced_quantity || 0);
                      return (
                        <tr key={l._po_line_index} className="border-b border-slate-100">
                          <td className="py-2 px-2">
                            <div className="font-bold text-slate-900">{l.description}</div>
                            <div className="text-[10px] text-slate-500">{l.item_code} · {l.unit} · ₹{l.rate}/{l.unit} · GST {l.gst_rate}%</div>
                          </td>
                          <td className="py-2 px-2 text-right tabular">{l.quantity}</td>
                          <td className="py-2 px-2 text-right tabular text-slate-500">{pulled || "—"}</td>
                          <td className={`py-2 px-2 text-right tabular font-bold ${remaining > 0 ? "text-emerald-700" : "text-slate-400"}`}>{remaining}</td>
                          <td className="py-2 px-2">
                            <input type="number" step="0.01" min="0" max={remaining}
                              disabled={remaining <= 0}
                              value={pullQty[l._po_line_index] || ""}
                              onChange={e => {
                                const v = Math.min(Math.max(0, Number(e.target.value) || 0), remaining);
                                setPullQty(q => ({ ...q, [l._po_line_index]: v === 0 ? "" : v }));
                              }}
                              data-testid={`po-picker-qty-${l._po_line_index}`}
                              className="w-full px-1.5 py-1 text-right text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"/>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-4 flex justify-end gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                  <button type="button" onClick={() => {
                    const q = {};
                    (selectedPo.line_items || []).forEach(l => {
                      if ((l._remaining_quantity || 0) > 0) q[l._po_line_index] = l._remaining_quantity;
                    });
                    setPullQty(q);
                  }} data-testid="po-picker-pull-all-remaining" className="font-bold hover:text-[#F97316] underline">Pull All Remaining</button>
                </div>
              </div>}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button type="button" onClick={onCancel} className="text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-slate-900">Cancel</button>
          <button type="button" onClick={confirm} disabled={!selectedPo}
            data-testid="po-picker-confirm-btn"
            className="bg-[#F97316] hover:bg-[#EA580C] disabled:bg-slate-300 text-white px-6 py-2 text-xs uppercase tracking-wider font-bold">
            Pull Selected Lines →
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, big }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600 uppercase tracking-wider text-[11px]">{label}</span>
      <span className={`tabular ${bold ? "font-bold" : ""} ${big ? "text-xl font-display font-black text-[#F97316]" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}


const compactInput = "w-full px-2 py-1 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white";

function CompactField({ label, required, children }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-[11px] text-slate-600 w-28 shrink-0 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// Sprint 3.11 — Searchable combobox for the RC (Contract) Number on
// Sales Invoice / CN / DN. Replaces the previous plain <select>, which
// forced the operator to scroll through every active project of the
// chosen customer. Now they can type any fragment of project code,
// project name OR contract number to filter the dropdown.
function RCNumberCombobox({ value, onChange, projects, disabled, compactInput, partnerSelected }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIdx, setActiveIdx] = React.useState(-1);
  const containerRef = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    function onDocClick(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const q = (query || "").trim().toLowerCase();
  const filtered = q
    ? projects.filter(p => {
        const code = (p.project_code || "").toLowerCase();
        const name = (p.project_name || "").toLowerCase();
        const contract = (p.project_contract_number || "").toLowerCase();
        return code.includes(q) || name.includes(q) || contract.includes(q);
      })
    : projects;

  const placeholder = !partnerSelected
    ? "Pick a customer first"
    : (projects.length
        ? `Search ${projects.length} active project(s) by code / name / contract…`
        : "No active projects for this customer");

  function pick(p) {
    onChange(p.project_contract_number || "");
    setQuery("");
    setOpen(false);
    setActiveIdx(-1);
  }

  function onKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(-1, i - 1));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && filtered[activeIdx]) {
        e.preventDefault();
        pick(filtered[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  // What to show inside the input box when CLOSED
  const inputDisplay = open ? query : (value || "");

  return (
    <div ref={containerRef} className="relative">
      <input
        data-testid="inv-rc-number"
        type="text"
        autoComplete="off"
        value={inputDisplay}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(""); } }}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
          // Allow free-text legacy fallback — store whatever they typed
          onChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
        className={compactInput}
      />
      {open && partnerSelected && (
        <div data-testid="inv-rc-number-dropdown"
             className="absolute z-30 left-0 right-0 mt-0.5 bg-white border border-slate-300 shadow-lg max-h-56 overflow-y-auto text-xs">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-slate-500 italic">No matches — keep typing for a legacy / off-master number.</div>
          ) : (
            filtered.map((p, idx) => (
              <button
                type="button"
                key={p.id}
                data-testid={`inv-rc-option-${p.id}`}
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`block w-full text-left px-3 py-1.5 hover:bg-slate-50 ${idx === activeIdx ? "bg-slate-100" : ""}`}
              >
                <div className="font-bold text-slate-800">{p.project_code}{p.project_name ? ` — ${p.project_name}` : ""}</div>
                <div className="text-[10px] text-slate-500">
                  {p.project_contract_number ? `Contract: ${p.project_contract_number}` : "No current contract"}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

